import {
  ContentType,
  Message,
  MessageFromClient,
  MessageToClient,
  CreateMailboxResponse,
  SendMessageRequest,
  CreateMailboxRequest,
  ClientHello,
  HelloErrorCode,
  RpcFrameType,
  KeyValPair,
  ChangeDataCaptureSubscription,
  MessageEnvelope,
  MailboxSubscription,
  SendReceipt,
  Ping,
  Subscription,
  ContinuumSubscription,
  Pong,
  RpcCallKind,
} from "./proto/wsmessages";
import { BuildInfo } from "./proto/pkg/discovery/discovery";

// The bundle's build identity, stamped by vite at build time (vite.config.ts define);
// undefined wherever the define is absent (vitest, storybook). A LABEL like tabUid —
// it rides the browserSession hello so the browser's processrun carries codebase,
// version and commit like every other client's.
// FEATURE-20260802-browser-bundle-build-identity.
export function bundleBuildInfo(): BuildInfo | undefined {
  try {
    if (typeof __A8_BUILD_INFO__ === "undefined" || !__A8_BUILD_INFO__) return undefined;
    // `customer` (the profile the bundle was built for) is stamped in the bundle
    // but has no BuildInfo proto field yet, and fromPartial's Exact type refuses
    // an extra key; carrying it in the hello is a godev proto change
    // (FEATURE-20260905-react-playground-customer-profiles, follow-up).
    const { customer: _customer, customerCommit: _customerCommit, ...buildInfo } = __A8_BUILD_INFO__;
    return BuildInfo.fromPartial(buildInfo);
  } catch {
    return undefined;
  }
}
import { tailChannel } from "./channel-tail";
import { memoize } from "../Utils";
import { MailboxAddress, LogicalMailbox } from "./types";
import { UrlBuilder } from "../pages/qubes/UrlBuilder";
import { config } from "../config/app-config";
import { AuthTokenManager, onAuthTokenSet } from "../services/auth-token-manager";
// Your code here

export interface ContentTypeHandler {
  readonly webSocketUrlPath: string;
  sendMessageFromClient(mfc: MessageFromClient, ws: WebSocket): void;
}

const JsonContentTypeHandler: ContentTypeHandler = {
  webSocketUrlPath: "/api/ws/send_receive_json",

  sendMessageFromClient(mfc: MessageFromClient, ws: WebSocket): void {
    const obj = MessageFromClient.toJSON(mfc);
    const jsonStr = JSON.stringify(obj);
    ws.send(jsonStr);
  },
};

const ProtobufContentTypeHandler: ContentTypeHandler = {
  webSocketUrlPath: "/api/ws/send_receive_proto",
  sendMessageFromClient(mfc: MessageFromClient, ws: WebSocket): void {
    const bytes = MessageFromClient.encode(mfc).finish();
    ws.send(bytes);
  },
};

export const ContentTypeHandlerWrapper = {
  Json: JsonContentTypeHandler,
  Protobuf: ProtobufContentTypeHandler,
};

export function newHermesClient(
  rootUrl: string,
  contentTypeHandler: ContentTypeHandler,
  authToken?: string
): HermesClient {
  const hci = new HermesClientImpl(rootUrl, contentTypeHandler, authToken);

  hci.mailbox().then((mbox) => {
    const correlations = hci.correlations;
    hci.channelMessageSubscribe(
      {
        id: "rpc-inbox",
        state: "rpc-inbox",
        readerKey: mbox.readerKey,
        channel: "rpc-inbox",
        startSeq: "all",
        endSeq: "tail",
      },
      (me, msg) => {
        // msg is already decoded and has any dataRef resolved by channelMessageSubscribe.
        const endPoint = msg.header?.rpcHeader?.endPoint;
        if (
          msg.header?.rpcHeader?.frameType === RpcFrameType.Request &&
          endPoint === "ping"
        ) {
          hci.sendPongResponse(mbox, msg, endPoint);
        } else {
          const correlationId = msg.header?.rpcHeader?.correlationId;
          if (correlationId) {
            const resolve = correlations.get(correlationId);
            if (resolve !== undefined) {
              const keepAlive = resolve(msg);
              // If listener returns false, keep the correlation for streaming
              // If listener returns true or undefined, delete it (default behavior)
              if (keepAlive !== false) {
                correlations.delete(correlationId);
              }
            }
          }
        }
      }
    );
  });

  // Ping and liveness run on SEPARATE timers — see the cadence constants for why the check
  // must not inherit the ping's granularity. The ping generates the inbound that proves the
  // connection is alive; the check decides when the absence of inbound has gone on too long.
  setInterval(() => {
    hci.sendPing();
  }, pingPeriodMs);
  setInterval(() => {
    hci.checkLiveness(livenessDeadlineMs);
  }, livenessCheckPeriodMs);

  // Fast WiFi-migration detection: on WAKE (tab becomes visible), actively probe
  // whether our server-seen IP changed and rebuild the WS immediately if so,
  // instead of waiting ~75s for the liveness timeout to notice a dead socket.
  // The check is idempotent and IP-change-gated, so a benign wake is a cheap
  // no-op. Seed lastKnownIp with one probe at startup so the first real change is
  // detectable. FEATURE-20260715-browser-fast-detection-of-migrated-wifi.
  if (typeof document !== "undefined") {
    void hci.verifyTransport(); // seed lastKnownIp
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        void hci.verifyTransport();
      }
    });
    // focus is a secondary wake signal (some browsers fire it without a
    // visibility transition, e.g. alt-tab back to an already-visible tab).
    window.addEventListener("focus", () => {
      void hci.verifyTransport();
    });
  }
  return hci;
}

/**
 * Create the mailbox
 * @param channels
 * @param rootUrl
 * @param authToken
 * @returns
 */
// newProcessUid mints a client-side processrun uid for a browser session — the same
// pattern a daemon uses (it generates its own and passes it into both ProcessStart
// and its mailbox). 32 lowercase-hex chars to fit the varchar(32) uid columns.
export function newProcessUid(): string {
  return crypto.randomUUID().replace(/-/g, "").slice(0, 32);
}

// tabUid is the IMMUTABLE per-tab correlation id: stable for the tab's whole life
// across mailbox churn and re-mint. See FEATURE-20260714-browser-session-mailbox
// -accounting. Persisted in sessionStorage (per-tab, not shared across tabs) so it
// ALSO survives the full navigation an auth-expiry re-login performs — the tab is
// the same tab before and after re-auth, so its correlation id must not change
// (FEATURE-20260715-browser-auth-expiry-relogin). A duplicated tab still gets its
// own: sessionStorage is not copied to a duplicated tab in modern browsers, and a
// fresh tab has none, so it mints one.
const TAB_UID_KEY = "a8.tabUid";
function resolveTabUid(): string {
  try {
    const existing = sessionStorage.getItem(TAB_UID_KEY);
    if (existing) return existing;
    const minted = newProcessUid();
    sessionStorage.setItem(TAB_UID_KEY, minted);
    return minted;
  } catch {
    // sessionStorage unavailable (rare: privacy mode / SSR) — fall back to a
    // per-load id. Loses cross-navigation stability but never breaks.
    return newProcessUid();
  }
}
const tabUid: string = resolveTabUid();

// browserMainName labels which browser app/entry point this is; overridable per
// deployment. Lands in the processrun so browser apps are distinguishable.
const browserMainName: string =
  (globalThis as { __A8_BROWSER_MAIN__?: string }).__A8_BROWSER_MAIN__ ??
  "react-playground";

// THE LIVENESS CADENCE — how fast this client notices a socket that died WITHOUT a close.
// That is the half-open case, and also what a middlebox creates when it drops its upstream
// but leaves the browser's own socket up; the client sees no error, only silence.
//
// pingPeriodMs is what guarantees inbound: the server answers pings and sends its own, so a
// live connection is never silent much longer than one period. livenessDeadlineMs is 2.5
// periods, so a single missed round trip is not a verdict.
//
// livenessCheckPeriodMs is SEPARATE from pingPeriodMs deliberately. While one timer did both,
// detection granularity was a whole ping period — a 75s deadline was first TESTED at 90s.
// That was measured, not theorised: react-playground/half-open recovered at maxLatencyMs
// ~91_000 on every gauntlet run for weeks. Its own faster timer costs one comparison and no
// network, and hands that whole period back.
//
// WHY 10s, WHERE THIS USED TO PING AT 30s: until the deadline fires, a tab behind a broken
// middlebox renders stale data and looks live. At a 30s ping that lasted ~91 seconds. This is
// an ops UI, so a tab that looks current and is not is the dangerous failure mode.
//
// NOT coupled to the gateway's own inbound-silence deadline (WebSocketTimeoutInMs, 90s =
// three missed 30s pings). Pinging more often only makes that easier to satisfy, so the
// gateway is left alone on purpose — tightening it would tear down hermes, which does not
// ping at all. Detection also stays far inside browserAwolWindowMs below, so recovery is a
// cheap resumeSession rather than a re-mint.
// BUG-20260809-react-playground-partition-heal-never-reconnects
export const pingPeriodMs = 10_000;
export const livenessDeadlineMs = pingPeriodMs * 2.5;
export const livenessCheckPeriodMs = 5_000;

// What the two periods above actually buy: the deadline, plus up to one check period of
// granularity before the check that exceeds it runs. Named so the budget is a value a test
// can pin, rather than a number every reader has to re-derive from three constants.
export const worstCaseSilentDetectionMs =
  livenessDeadlineMs + livenessCheckPeriodMs;

// HOW LONG A DIAL MAY SIT IN CONNECTING BEFORE IT IS PRESUMED SWALLOWED.
//
// Detecting a dead socket (above) only helps if the reconnect that follows can itself make
// progress. A dial onto a path that SWALLOWS packets — a blackhole, a middlebox that stopped
// forwarding, a captive portal, a NAT that dropped its state — gets no FIN and no RST, so
// neither onerror nor onclose ever fires. Without this timeout the retry ladder in
// newHermesConnection never starts: the socket sits in CONNECTING until the OS gives up, and
// because the reconnecting guard is released only by onopen, the whole client stays wedged.
//
// That is measured, not theorised. gauntlet run 20260809-180136, react-playground/half-open:
// the client detected silence at 29s, dialed into the harness's 80s blackhole, and never
// recovered — received 2345 of 20000, reconnects=0, on all three samples. The blackhole even
// lifted at ~80s with 70s of window to spare and the hung dial did not notice.
// BUG-20260810-hermes-client-wedges-on-a-blackholed-reconnect-dial.
//
// 10s is set against the two windows this must live inside. Long enough that a merely slow
// network is not mistaken for a dead one — and a retry there costs nothing anyway, the
// backoff absorbs it. Short enough that repeated attempts keep probing while an outage
// drains: against the 80s blackhole above, attempts land at roughly 10s, 20s, 31s, 43s and
// 57s of dialing, so the path is re-tested several times inside half-open's 150s window
// rather than once.
export const connectTimeoutMs = 10_000;

// browserAwolWindowMs sits BELOW the server's browser processrun AWOL window
// (pingTimeoutSeconds = 300s). The invariant (glen): client timeout < server
// timeout — the client must re-mint BEFORE the server would have reaped, never
// after. If the two were equal, a client that judged itself just-inside the window
// could RESUME a processUid the server already reclaimed. 240s = 4 min gives a
// safe margin under the server's 5 min. If the connection is silent longer than
// this, the processrun is (or is about to be) AWOL-reclaimed, so on reconnect we
// re-mint a fresh session rather than re-announce a dead processUid.
const browserAwolWindowMs = 240_000;

// After this many consecutive re-mints that never CONFIRM a session (no real
// inbound from the server since the mint), stop minting fresh mailboxes and back
// off — each re-mint is a dead mailbox+stream, and a session that never got going
// cannot be fixed by minting another. One stuck tab did this ~every 65s for 8h on
// 2026-07-07, leaving 459 dead mailboxes. BUG-20260719-browser-remint-loop-...
const MAX_UNCONFIRMED_REMINTS = 10;

// If an RPC hasn't returned within this window, treat the stall as a possible
// dead-transport signal and fire verifyTransport() (which rebuilds the WS iff our
// server-seen IP moved). Short enough to beat the ~75s liveness timeout by a wide
// margin; long enough not to false-fire on a genuinely slow-but-alive request.
// FEATURE-20260715-browser-fast-detection-of-migrated-wifi.
const RPC_STALL_PROBE_MS = 4_000;

// A streaming call has no total ceiling — a legitimate stream can run for as long as
// it has things to say — so what bounds it is SILENCE in both directions. The producer
// sends a Heartbeat every third of this while it has nothing else to send, so two lost
// heartbeats are not a verdict. Matches the Go client's StreamIdleDeadline.
// FEATURE-20260901-mesh-streaming-rpc.
const RPC_STREAM_IDLE_MS = 90_000;

// Hard ceiling on a single RPC. The stall probe above only rebuilds the transport;
// it deliberately does NOT settle the request, so before this existed a reply that
// never arrived (socket dropped mid-flight, server never answered) left the caller
// awaiting a promise that could not settle — the correlations map holds only the
// RESOLVE side, so there was no path to failure at all. The SQL query page showed
// this as "Streaming results... 0 rows received so far" forever, with no error and
// no way to retry. Generous on purpose: this is a backstop for a DEAD request, not
// a latency budget — a slow-but-alive query must still win.
// See tracker FEATURE-20260724-hermes-ws-reconnect-and-inflight-resend.
const RPC_TIMEOUT_MS = 90_000;

// rpcTimeoutMs resolves the hard ceiling for one unary call: the caller's
// RawRpcRequest.timeoutMs when it is a positive finite number, RPC_TIMEOUT_MS
// otherwise. A caller whose endpoint legitimately runs for minutes (a tool-heavy
// chat turn) raises its own ceiling without moving the default for every other RPC.
export function rpcTimeoutMs(requested: number | undefined): number {
  return requested !== undefined && Number.isFinite(requested) && requested > 0
    ? requested
    : RPC_TIMEOUT_MS;
}

// Reject `promise` if it has not settled within ms. The timer is always cleared,
// so a settled promise leaves nothing pending; the losing side of the race is
// harmless either way (a late resolve has no one listening).
function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  message: string,
): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }) as Promise<T>;
}

// transportAction is the PURE decision at the heart of verifyTransport, factored
// out so the rebuild/re-mint matrix is unit-testable without a live connection.
// Given the previously-known server-seen IP, a freshly-probed IP, and how long
// we've been silent, decide what to do:
//   - "none"    : first probe (prev undefined), unchanged IP, or a failed probe
//                 (ip undefined) — do nothing, leave it to the liveness backstop.
//   - "resume"  : IP changed but still INSIDE the AWOL window — rebuild the WS,
//                 keep the same session (processUid + mailbox + auth).
//   - "remint"  : IP changed AND past the AWOL window — rebuild + re-mint (the old
//                 processrun is gone).
// Rebuild-WS and re-mint are INDEPENDENT (glen): IP change decides REBUILD; the
// AWOL window alone decides RE-MINT. FEATURE-20260715-browser-fast-detection-of
// -migrated-wifi.
export function transportAction(
  prevIp: string | undefined,
  probedIp: string | undefined,
  silentForMs: number,
  awolWindowMs: number = browserAwolWindowMs
): "none" | "resume" | "remint" {
  // The AWOL window ALONE decides re-mint — this check comes FIRST, before the ip
  // comparisons, because that is what the contract above says and the old order
  // quietly broke it: an unchanged ip returned "none" without ever consulting the
  // window. A lid closed for hours wakes on the SAME wifi with the SAME ip, and its
  // mailbox is exactly as dead as one that moved networks.
  // BUG-20260825-rpc-responder-floods-on-reclaimed-mailbox.
  if (silentForMs >= awolWindowMs) return "remint";
  if (probedIp === undefined) return "none"; // probe failed
  if (prevIp === undefined || prevIp === probedIp) return "none"; // first probe / unchanged
  return "resume"; // moved, still inside the window
}

// suspensionAction — the PURE "was this machine asleep?" decision, factored out in
// the same style as transportAction so it is unit-testable without a live client.
//
// WHY A SEPARATE SIGNAL AT ALL. Every other health check this client runs watches the
// SOCKET: sendPing generates inbound, checkLiveness measures silence since the last
// inbound, verifyTransport probes the server-seen ip. A slept laptop defeats all
// three — on wake the socket re-establishes and pings resume, so `silentFor` reads
// ~0 and the ip is unchanged, while the MAILBOX died hours ago (the server AWOLs the
// processrun at 300s and the ledger reaper deletes its rpc-inbox stream). The client
// then fires refresh queries into a mailbox that can never answer. Measured on
// continuum-prod: 5,765 such queries over 1h47m, peaking at ~59/sec.
//
// The suspension itself is the tell. The liveness timer is supposed to fire every
// livenessCheckPeriodMs; timers do not run while the machine is suspended, so an
// oversized WALL-CLOCK gap between ticks is precisely how long we were away — a
// measurement no socket-based signal can make.
//
// Re-mint rather than validate, deliberately: the costs are asymmetric. A false
// positive (a throttled/frozen background tab that was never really dead) costs one
// extra mailbox mint, and mailboxes are short-lived by design; a false negative
// costs the flood above. remintDecision's runaway guard bounds the former.
export function suspensionAction(
  wallGapMs: number,
  awolWindowMs: number = browserAwolWindowMs
): "none" | "remint" {
  return wallGapMs >= awolWindowMs ? "remint" : "none";
}

// observedSilenceMs — how much of the silence since the last inbound this client actually
// had a chance to observe. The PURE half of checkLiveness's verdict, factored out so the
// throttled-tab arithmetic is unit-testable without a live socket.
//
// Chrome throttles a hidden tab's timers to ONE wake per minute after five minutes. Both
// timers here (ping and check) fire in that one wake, and the pong that answers the ping
// lands after the wake's synchronous code has run. So on every wake the check measured
// ~58s of silence against a 25s deadline and called a healthy socket dead: close, resume,
// and — via onclose — a second resume that orphaned the first. One open socket per minute
// per hidden tab, held forever by the gateway's own pings. 304 of them from one
// workstation on 2026-09-14, at which point Chrome queued every new dial behind them and
// the VISIBLE tab starved too. BUG-20260912-hidden-tab-leaks-a-gateway-socket-per-minute.
//
// The rule: silence the client could not have broken is not evidence. The ping is what
// generates inbound, and the ping did not run while the tab slept — so the part of the
// tick gap beyond the nominal check period is discounted. A visible tab has no excess gap
// and keeps today's verdict exactly. A hidden tab with a LIVE socket reads ~5s observed
// per wake (the pong from the previous wake refreshed lastInboundAt) and stays connected.
// A hidden tab with a DEAD socket accumulates: the unanswered ping from wake N leaves
// silence that wake N+1 cannot explain, so it is caught on the second wake (~120s) —
// still a resume, well inside browserAwolWindowMs.
export function observedSilenceMs(
  silentForMs: number,
  wallGapMs: number,
  checkPeriodMs: number = livenessCheckPeriodMs
): number {
  const unobservedMs = Math.max(0, wallGapMs - checkPeriodMs);
  return Math.max(0, silentForMs - unobservedMs);
}

// closedSocketAction — what onclose should do for the socket that just closed. The PURE
// decision, factored out for the same reason as dialDecision.
//
// The second half of the leak above: the liveness watchdog closes a socket and dials a
// resume; the closed socket's onclose fires two seconds later, by which time the resume
// has OPENED and released the reconnecting guard, so onclose dials a second resume and
// replaces currentConn. The first resume is abandoned open — it answers the gateway's
// native pings from the network layer with no JavaScript involved, so nothing ever reaps
// it. onclose on a socket that is no longer the current one is the tail of a replacement
// that already happened; only the CURRENT socket closing is news that needs a redial.
// An unknown current socket (nothing has opened yet) never suppresses — a false "ignore"
// would be a wedge, and that is the worse failure.
export function closedSocketAction(
  closed: unknown,
  current: unknown
): "reconnect" | "ignore" {
  if (current === undefined) return "reconnect";
  return closed === current ? "reconnect" : "ignore";
}

// retirePreviousSocket — THE ONE-SOCKET INVARIANT. When a socket opens and becomes the
// client's socket, the socket it replaces is closed, here, no matter which path asked for
// the reconnect.
//
// Before this, every close lived in a CALLER (the liveness watchdog, the suspension check,
// verifyTransport) and reconnect() itself replaced currentConn without closing anything. So
// any path that reached reconnect while a live socket was current abandoned that socket
// open — the close-handler redial was just the path that did it on 2026-09-14, 304 times.
// Closing the replaced socket where the replacement happens makes a second open socket
// impossible rather than making each known path careful. closedSocketAction is what stops
// the retired socket's own onclose from dialling again.
// BUG-20260912-hidden-tab-leaks-a-gateway-socket-per-minute.
export type RetirableSocket = { readyState: number; close(): void };

// WebSocket.CONNECTING / OPEN, stated as literals so the rule does not depend on a global
// WebSocket existing where it runs (tests, workers).
const SOCKET_CONNECTING = 0;
const SOCKET_OPEN = 1;

export function retirePreviousSocket(
  previous: RetirableSocket | undefined,
  opened: RetirableSocket
): boolean {
  if (previous === undefined || previous === opened) return false;
  if (previous.readyState !== SOCKET_CONNECTING && previous.readyState !== SOCKET_OPEN) {
    return false; // already closing or closed — nothing left to release
  }
  try {
    previous.close();
  } catch {
    /* ignore — a socket that cannot be closed is not ours to keep either */
  }
  return true;
}

// remintDecision — the PURE runaway-loop guard, factored out so reconnect()'s
// re-mint policy is unit-testable without a live connection. Given whether the
// reconnect wants a re-mint and how many consecutive re-mints have gone by WITHOUT
// confirming a session (no real inbound since the mint), decide:
//   - "mint"     : re-mint a fresh session (the normal AWOL-recovery case)
//   - "backoff"  : the re-mint cap is hit — do NOT mint (it would be a dead
//                  mailbox), retry the SAME mailbox with a delay instead
//   - "reconnect": not a re-mint — just rebuild the WS on the same session
// BUG-20260719-browser-remint-loop-unbounded-dead-mailboxes.
export function remintDecision(
  remint: boolean,
  unconfirmedRemints: number,
  maxUnconfirmedRemints: number = MAX_UNCONFIRMED_REMINTS
): "mint" | "backoff" | "reconnect" {
  if (!remint) return "reconnect";
  return unconfirmedRemints >= maxUnconfirmedRemints ? "backoff" : "mint";
}

// rekeyOwnMailboxSubscriptions — the re-mint half of the subscription story, factored
// out pure (same shape as remintDecision above) so it is unit-testable without a socket.
//
// WHY IT EXISTS. newHermesClient registers the session's rpc-inbox subscription — the one
// carrying the session's own readerKey — inside `hci.mailbox().then(...)`, attached ONCE to
// the FIRST mint. A re-mint (mintBrowserSession: liveness gap past the AWOL window, lid
// closed, tab frozen) replaces that promise and nothing subscribed again, so the old
// subscription rode along in activeSubscriptions still keyed to the PREVIOUS session's
// readerKey. Every gateway connection of the new session then read the previous session's
// stream, and the new session's own inbox had zero readers: requests executed and were
// acked, replies were published, nobody read them — "RPC timed out after 90000ms" with
// ping/pong still flowing. Measured on continuum-prod 2026-09-12: four deaf sessions on one
// tab in a morning; the archived inbox of one held every reply, unread.
// BUG-20260912-reminted-browser-session-never-subscribes-its-inbox.
//
// Rewrites IN PLACE every mailbox subscription keyed to previousReaderKey so it names
// newReaderKey, and returns those subscriptions for the caller to re-send on the new
// session's socket. Only the session's OWN key is touched: a devtools observer on some
// other mailbox (rpcObserverSubscribe) is keyed to that mailbox and must stay put.
//
// A NUMERIC startSeq is reset to "all": it was a position in the OLD stream, and the new
// inbox starts at 1 — carrying the mark forward would make the gateway start past the
// new stream's head and advanceResumePosition drop every reply below it as a stale
// replay. The symbolic starts ("first"/"all"/"tail"/"new") are stream-independent and kept.
export function rekeyOwnMailboxSubscriptions(
  subs: Iterable<ActiveSubscription>,
  previousReaderKey: string,
  newReaderKey: string
): Subscription[] {
  const rekeyed: Subscription[] = [];
  for (const as of subs) {
    const ms = as.protoSubscription.mailbox;
    if (ms === undefined || ms.readerKey !== previousReaderKey) continue;
    ms.readerKey = newReaderKey;
    if (ms.startSeq !== undefined && /^[0-9]+$/.test(ms.startSeq)) {
      ms.startSeq = "all";
    }
    rekeyed.push(as.protoSubscription);
  }
  return rekeyed;
}

// helloSubscriptions — what a hello may carry. A MINT hello goes out before the new
// readerKey exists, so a mailbox subscription still keyed to the previous session
// (staleReaderKey) must NOT ride it: the gateway would register a reader on the previous
// stream — "is gone (mailbox reclaimed) — ending the subscription" when reaped, a reader
// on the wrong stream when not. Those are re-sent, re-keyed, once ClientSessionStarted
// names the new key (rekeyOwnMailboxSubscriptions). Everything else — CDC, continuum,
// observers of other mailboxes — rides every hello as before. No stale key: pass all.
export function helloSubscriptions(
  subs: Iterable<ActiveSubscription>,
  staleReaderKey: string | undefined
): Subscription[] {
  const out: Subscription[] = [];
  for (const as of subs) {
    const ms = as.protoSubscription.mailbox;
    if (staleReaderKey !== undefined && ms !== undefined && ms.readerKey === staleReaderKey) {
      continue;
    }
    out.push(as.protoSubscription);
  }
  return out;
}

// Whether a dial may proceed, split out for the same reason remintDecision is:
// the policy is unit-testable without a live connection.
//   - "in-flight"   : a (re)connect is already running; onclose and the liveness
//                     watchdog both call reconnect() and must not stack.
//   - "auth-halted" : the gateway refused the hello as UNAUTHENTICATED. Redialling
//                     cannot mint a token, so the client stops entirely and waits
//                     for AuthTokenManager to announce a fresh one. NOT a backoff:
//                     a slower retry of something that can never succeed is still
//                     a loop.
//   - "dial"        : go.
// BUG-20260910-ws-onclose-redials-through-unauthenticated.
export function dialDecision(
  reconnecting: boolean,
  authHalted: boolean
): "dial" | "in-flight" | "auth-halted" {
  if (reconnecting) return "in-flight";
  if (authHalted) return "auth-halted";
  return "dial";
}

// THE RECONNECT BACKOFF LADDERS — both FULL-JITTERED, per docs/mesh-client/client-cadence.md
// section 6 (invariant 8). Every reconnect event that matters is CORRELATED: a gateway deploy
// disconnects every client at once, an office AP reboot drops every browser behind one NAT
// together. Deterministic exponential backoff synchronizes that herd — clients that failed
// together retry together, and at the cap they stay phase-locked indefinitely, dialing in
// lockstep exactly when the gateway is weakest (mid-restart). Full jitter
// (delay = rand(0, min(cap, base * 2^n))) costs a human a few hundred invisible milliseconds
// and de-phases the herd; each ladder keeps its own base/cap, the jitter is the shared shape.
// FEATURE-20260815-ws-client-liveness-spec.
function fullJitterMs(
  baseMs: number,
  exponent: number,
  capMs: number,
  rand: () => number
): number {
  return Math.round(rand() * Math.min(baseMs * Math.pow(2, exponent), capMs));
}

// The dial ladder: how long to wait after failed connect attempt `attempt` (1-based).
// Ceiling 0.5s, 1s, 2s, … capped at 10s — the cap stays inside connectTimeoutMs-scale
// probing so a draining outage is re-tested several times, not once (see connectTimeoutMs).
export const dialBackoffBaseMs = 500;
export const dialBackoffCapMs = 10_000;
export function dialBackoffDelayMs(
  attempt: number,
  rand: () => number = Math.random
): number {
  return fullJitterMs(dialBackoffBaseMs, attempt - 1, dialBackoffCapMs, rand);
}

// The re-mint runaway-loop backoff: how long a tab past MAX_UNCONFIRMED_REMINTS waits
// before probing the SAME mailbox again, given how far past the cap it is (0-based).
// Ceiling 30s, 60s, … capped at 5 min, so a stuck tab probes occasionally instead of
// minting a dead mailbox every ~65s. BUG-20260719-browser-remint-loop-unbounded-dead-mailboxes.
export const remintBackoffBaseMs = 30_000;
export const remintBackoffCapMs = 300_000;
export function remintBackoffDelayMs(
  overshoot: number,
  rand: () => number = Math.random
): number {
  return fullJitterMs(remintBackoffBaseMs, overshoot, remintBackoffCapMs, rand);
}

// DialableSocket — the sliver of WebSocket that openSocketWithRetry touches, so the dial
// ladder can be driven by a fake in a test without standing up a real socket.
// The handler parameters are `any` deliberately: the DOM types these as Event / CloseEvent,
// and under strictFunctionTypes a narrower declared parameter here would make WebSocket fail
// to satisfy the constraint. This type is a structural view OF WebSocket, not a rival to it.
export type DialableSocket = {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  onopen: ((ev: any) => unknown) | null;
  onerror: ((ev: any) => unknown) | null;
  onclose: ((ev: any) => unknown) | null;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  close(): void;
};

// openSocketWithRetry — dial until a socket OPENS, and treat "never answered" as a failure
// rather than as something to wait on forever.
//
// Factored out of newHermesConnection for the same reason as remintDecision above: the rule
// it encodes is the one that broke, and it could not be tested while it lived inside a
// private method holding a live connection.
//
// THE RULE: every attempt has three ways out and all three must lead somewhere. onerror and
// onclose route to the backoff (a path that REFUSES a dial). The watchdog routes a dial that
// neither opened nor failed — a path that SWALLOWS it — into the same backoff. Before the
// watchdog existed that third case had no exit at all, and one blackholed dial wedged the
// client permanently: no retry, no watchdog, no recovery short of a reload.
// BUG-20260810-hermes-client-wedges-on-a-blackholed-reconnect-dial.
//
// onAttemptFailed reports the failure and returns how long to wait before re-dialing, so the
// backoff policy and its attempt counter stay with the caller that owns them.
export function openSocketWithRetry<T extends DialableSocket>(
  makeSocket: () => T,
  onAttemptFailed: () => number,
  timeoutMs: number = connectTimeoutMs
): Promise<T> {
  return new Promise<T>((resolve) => {
    const attempt = () => {
      let socket: T;
      try {
        socket = makeSocket();
      } catch {
        // A constructor that throws (a malformed URL, a blocked scheme) is just another
        // failed attempt — it must not reject this promise, or the reconnecting guard is
        // latched behind a rejection nobody awaits. No watchdog is armed yet, so this
        // schedules the next attempt directly.
        setTimeout(attempt, onAttemptFailed());
        return;
      }

      let settled = false;

      const watchdog = setTimeout(() => {
        if (settled) return;
        console.log(
          `hermes websocket connect did not answer within ${timeoutMs}ms — presuming the path swallowed it, retrying`
        );
        // Close first: the socket is still CONNECTING, and abandoning it without closing
        // leaks a pending connection per attempt.
        try {
          socket.close();
        } catch {
          /* ignore */
        }
        retry();
      }, timeoutMs);

      const retry = () => {
        if (settled) return;
        settled = true;
        clearTimeout(watchdog);
        setTimeout(attempt, onAttemptFailed());
      };

      socket.onerror = retry;
      socket.onclose = retry;
      socket.onopen = () => {
        if (settled) return;
        settled = true;
        clearTimeout(watchdog);
        resolve(socket);
      };
    };
    attempt();
  });
}

// advanceResumePosition — the PURE high-water rule for subscription resume, factored out
// so it is unit-testable without a socket (the transportAction/remintDecision shape).
// Invariant 2 of godev docs/mesh-client/client-invariants.md.
//
// The stored value on a subscription's startSeq is the NEXT WANTED sequence, i.e.
// lastDelivered + 1 — because a numeric startSeq is INCLUSIVE gateway-side, storing the
// last-delivered value re-asserted an already-seen sequence and redelivered one message
// per subscription per reconnect (channel-tail.ts:27 states the same +1 convention).
// Storing was also last-write-wins, so a redelivered LOWER sequence — legal at-least-once
// input — regressed the mark and widened the next replay.
//
// Given the currently stored startSeq (possibly "first"/"all"/undefined — no mark yet)
// and a delivered frame's sequence, returns:
//   - startSeq: the new value to store (delivered+1), or undefined to leave it alone
//   - fresh:    false when the frame is at or below the mark — a stale replay the app
//               has already seen, to be dropped, never delivered twice
// Sequences are uint64 carried as strings (ts_proto forceLong=string): compared as
// BigInt, never as strings — "9" > "10" lexicographically.
// BUG-20260801-react-playground-resume-off-by-one.
export function advanceResumePosition(
  storedStartSeq: string | undefined,
  deliveredSeq: string | undefined
): { startSeq?: string; fresh: boolean } {
  if (!deliveredSeq) return { fresh: true }; // unattributable — pass through untracked
  let seq: bigint;
  try {
    seq = BigInt(deliveredSeq);
  } catch {
    return { fresh: true };
  }
  if (seq <= 0n) return { fresh: true };

  // The stored position, when it is numeric. "first"/"all"/"tail" mean no mark yet.
  let nextWanted: bigint | undefined;
  if (storedStartSeq) {
    try {
      const n = BigInt(storedStartSeq);
      if (n > 0n) nextWanted = n;
    } catch {
      // non-numeric bootstrap start — no mark yet
    }
  }

  if (nextWanted !== undefined && seq < nextWanted) {
    return { fresh: false }; // seq <= mark (mark = nextWanted-1): stale replay, drop
  }
  return { startSeq: String(seq + 1n), fresh: true };
}

// retireOnSendMessageResponse — the rule for when a SendMessageResponse actually RETIRES a
// send, factored out pure so it is unit-testable without a socket (same shape as
// advanceResumePosition above).
//
// A RESPONSE CARRYING ERRORS IS NOT AN ACK. It is the gateway reporting that the message
// did not land — the everyday case being a send to a recipient that does not exist, which
// comes back as `no stream matches subject` because the routing table IS the existence
// check. Deleting on it throws away the only remaining copy and turns a REPORTED failure
// into a silent drop, in a system whose contract is dups-tolerated, drops-never. A failed
// send therefore stays in the buffer and is replayed on the next reconnect, exactly like a
// send that was never answered at all.
//
// A DUPLICATE is the opposite case and IS an ack: the stream is saying it already holds
// the message. Resending forever on that would be the mirror-image bug.
//
// PARTIAL FAILURE keeps the send, so recipients that did take it will see it twice. That
// is the deliberate trade — at-least-once tolerates the duplicate and cannot tolerate the
// drop.
// BUG-20260813-mesh-clients-retire-a-send-on-a-FAILED-send-response
export function retireOnSendMessageResponse(
  waitingForAck: Map<string, SendMessageRequest>,
  response:
    | {
        idempotentId?: string;
        errors?: { message?: string }[];
        duplicates?: string[];
      }
    | undefined
): void {
  const id = response?.idempotentId;
  if (!id) {
    return;
  }
  if (response?.errors !== undefined && response.errors.length > 0) {
    console.warn(
      `hermes client: send ${id} FAILED at the gateway (${response.errors.length} recipient(s), first: ${response.errors[0]?.message}) — keeping it for resend rather than discarding it`
    );
    return;
  }
  waitingForAck.delete(id);
}

export async function createMailbox(
  channels: string[],
  rootUrl: string,
  authToken?: string,
  processRunUid?: string
): Promise<CreateMailboxResponse> {
  // The int64 fields are typed as strings (ts_proto forceLong=string), so their
  // proto3 zero value is "0", not 0.
  const mbox: CreateMailboxRequest = {
    channels: channels,
    privateMetadata: {},
    publicMetadata: {},
    purgeTimeoutInMillis: "0",
    closeTimeoutInMillis: "0",
    extraData: {},
    // Link the mailbox to the browser session's processrun at creation (the daemon
    // pattern) so its streams reclaim via the processrun's AWOL. Empty for callers
    // that have no processrun. See FEATURE-20260714-browser-session-mailbox-accounting.
    processRunUid: processRunUid ?? "",
    // Browser mailboxes are short-lived. The field is new (the WS ClientHello path
    // carries lifecycle as an enum); this HTTP helper still serves its tests and the
    // not-yet-retired HTTP create surface, so it declares the coarse string form.
    lifecycleKind: "short-lived-cli",
  };
  // No auth token = do NOT mint. An anonymous mint SUCCEEDS (200) server-side
  // ("LEGACY anonymous mint") but the resulting mailbox is never identity-bound,
  // so every authenticated RPC through it is refused forever ("mailbox was never
  // authenticated") — an unrecoverable dead session. A logged-in tab always has a
  // token; an empty one at mint time means the session lost it (e.g. a WS reconnect
  // after a server restart raced the token). Treat it exactly like an expired token:
  // throw AuthExpiredError so the reconnect path routes to re-login instead of
  // silently minting an unbound mailbox. Sentinel status 0 = "no token to present"
  // (vs 401/403 = "token rejected"). BUG-20260721-browser-mailbox-queries-without-auth-bind.
  if (!authToken) {
    throw new AuthExpiredError(0);
  }

  const mboxObj = CreateMailboxRequest.toJSON(mbox);
  const mboxJson = JSON.stringify(mboxObj);

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    // X-Auth-Token is the current/canonical auth header (mesh AuthProvider
    // HeaderName). X-SESS / X-AUTH are LEGACY fallbacks the server still reads
    // (mesh-main.go extractAuthToken) but new client code must not use.
    "X-Auth-Token": authToken,
  };

  let mailboxResponse: CreateMailboxResponse | undefined = undefined;
  const response = await fetch(`${rootUrl}/api/create_mailbox`, {
    method: "POST",
    headers: headers,
    body: mboxJson,
  });

  if (response.ok) {
    const responseJsonStr = await response.text();
    mailboxResponse = CreateMailboxResponse.fromJSON(
      JSON.parse(responseJsonStr)
    );
  } else if (response.status === 401 || response.status === 403) {
    // AUTH rejection (not transient): the token is gone/expired, so a re-mint
    // can never succeed — the only recovery is re-login. Throw a DISTINCT error
    // so the reconnect path routes to the login flow instead of retrying
    // forever. See FEATURE-20260715-browser-auth-expiry-relogin.
    throw new AuthExpiredError(response.status);
  } else {
    throw new Error(`createMailbox failed with status ${response.status}`);
  }

  return mailboxResponse;
}

// sendProcessStart announces this browser session's processrun as the FIRST action
// on load — before the mailbox or WS exist — so the row is created (server-side, by
// an authenticated user) up front and the mailbox/streams then reference an owner that
// already exists. Auth-required: the server validates the token so it knows this is a
// real user. Fire-and-forget (async, non-blocking): a lost announce degrades to the
// data-bug the monitor flags, not a startup failure. See
// TASK-20260719-browser-processstart-before-mailbox.
export async function sendProcessStart(
  rootUrl: string,
  processUid: string,
  tabUid: string,
  mainName: string,
  authToken?: string
): Promise<void> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (authToken) {
    // X-Auth-Token is the current/canonical auth header; X-SESS is legacy.
    headers["X-Auth-Token"] = authToken;
  }
  const body = JSON.stringify({
    processUid,
    tabUid,
    mainName,
    extraData: { url: globalThis.location?.href ?? "" },
  });
  const response = await fetch(`${rootUrl}/api/process_start`, {
    method: "POST",
    headers,
    body,
  });
  if (!response.ok) {
    throw new Error(`process_start failed with status ${response.status}`);
  }
}

// AuthExpiredError marks a mailbox-mint failure that is an AUTHENTICATION
// rejection (HTTP 401/403), distinct from a transient/network failure. The
// reconnect path treats it specially: a re-mint cannot fix an expired token, so
// it hands off to onAuthExpired() (re-login) rather than backing off and retrying.
export class AuthExpiredError extends Error {
  constructor(public readonly status: number) {
    super(`createMailbox rejected: auth expired (status ${status})`);
    this.name = "AuthExpiredError";
  }
}

// Auth-expiry notification. The transport DOES NOT know about React or the login
// UI — it just clears the dead token and NOTIFIES listeners once. The app
// registers a listener at startup (onAuthExpired) that shows the re-login dialog;
// if none is registered (or it throws), a hard redirect to /auth/login is the
// fail-safe so a token expiry never leaves the tab silently wedged. Idempotent-
// guarded so concurrent reconnect attempts on one tab trigger the flow once.
let __authExpiredInFlight = false;
const __authExpiredListeners = new Set<() => void>();

// onAuthExpired registers a handler for "the session token expired; re-login is
// required." Returns an unsubscribe fn. The app wires its dialog here (App.tsx).
export function onAuthExpired(handler: () => void): () => void {
  __authExpiredListeners.add(handler);
  return () => __authExpiredListeners.delete(handler);
}

// stashReturnPath records where the user was so re-login bounces them back, via
// the app's own post-login channel (login-page.tsx reads sessionStorage
// "postLoginRedirect"). The tabUid is already persisted in sessionStorage, so it
// survives the provider handoff navigation and the resumed session stays the
// SAME tab. Exported so the dialog stashes right before it starts the flow.
export function stashReturnPath(): void {
  try {
    sessionStorage.setItem(
      "postLoginRedirect",
      window.location.pathname + window.location.search
    );
  } catch {
    /* sessionStorage unavailable — login still works, just no bounce-back */
  }
}

// Fire the auth-expired flow at most once per tab until a mint succeeds again.
//
// CONFIRM BEFORE DESTROYING. This used to clearToken() unconditionally on the
// first failed mint, which made a bad session UNRECOVERABLE: a mint can fail for
// reasons that have nothing to do with the token being dead (an unauthenticated
// hello because the client had no token to send, a server-side decode/validation
// error, a gateway blip), and wiping the credential guaranteed the retry failed
// the same way — a self-perpetuating logout. That is exactly how
// BUG-20260726-ws-createmailbox-401-wipes-valid-token presented: a perfectly
// valid 24h session destroyed on every login, with the user told it had
// "expired".
//
// validateToken() asks the server directly and is deliberate about the three
// outcomes: a definitive "not logged in" clears the token, an HTTP error clears
// it, and a NETWORK failure keeps it (unreachable != invalid). So we only reach
// re-login with the credential destroyed when the server actually disowned it.
//
// The re-login prompt still fires either way — a mint we cannot complete means
// this tab is not working, whatever the cause. What changes is that a live token
// survives to be retried instead of being thrown away on suspicion.
async function triggerAuthExpired(): Promise<void> {
  if (__authExpiredInFlight) return;
  __authExpiredInFlight = true;

  const authManager = AuthTokenManager.getInstance();
  if (authManager.getToken()) {
    let stillValid = false;
    try {
      // Clears the token itself iff the server says it is bad; keeps it on a
      // network error.
      stillValid = await authManager.validateToken();
    } catch (e) {
      // Never let the probe itself decide the outcome — an exception here is not
      // evidence the token is dead, so leave it in place.
      console.warn("hermes: auth check failed to complete, keeping token", e);
    }
    if (stillValid) {
      console.warn(
        "hermes: mailbox mint failed but the auth token is STILL VALID — keeping it and prompting re-login. " +
          "If this recurs, the mint is failing for a non-auth reason (check the gateway logs for the refused hello)."
      );
    } else {
      console.warn(
        "hermes: auth token rejected by the server — cleared, prompting re-login"
      );
    }
  } else {
    console.warn(
      "hermes: no auth token to present — prompting re-login"
    );
  }

  if (__authExpiredListeners.size === 0) {
    // No UI wired (e.g. a headless context) — fail safe to a hard redirect so
    // the tab never sits silently wedged.
    stashReturnPath();
    window.location.assign("/auth/login");
    return;
  }
  for (const l of __authExpiredListeners) {
    try {
      l();
    } catch (e) {
      console.error("hermes: onAuthExpired listener threw", e);
    }
  }
}

export interface ChangeDataCaptureEvent {
  id: string;
  schema: string;
  table: string;
  action: string;
  data: unknown;
  commitTime: string;
}

class Constants {
  static readonly rpcInboxChannelName = "rpc-inbox";
  static readonly rpcSentChannelName = "rpc-sent";
}

type MessageListener = (msg: Message) => void | boolean;

class HermesConnection {
  readonly clientImpl: HermesClientImpl;
  // undefined ONLY between socket-open and the ClientSessionStarted that mints the mailbox
  // on a fresh session. Populated by withMailbox() the moment those keys arrive; the RPC
  // path never sends before then (it awaits mailboxResponseP), so no send sees it unset.
  mailbox: CreateMailboxResponse | undefined;
  readonly webSocket: WebSocket;

  constructor(
    clientImpl: HermesClientImpl,
    mailbox: CreateMailboxResponse | undefined,
    webSocket: WebSocket
  ) {
    this.clientImpl = clientImpl;
    this.mailbox = mailbox;
    this.webSocket = webSocket;

    webSocket.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer) {
        this.onWebSocketBinaryMessage(event.data);
      } else {
        this.onWebSocketTextMessage(event.data);
      }
    };

    webSocket.onclose = (event) => {
      // code/reason/wasClean INLINE rather than the CloseEvent object: the wsconform driver
      // forwards this line as text, where an object renders as "JSHandle@object" and says
      // nothing. A clean close means the peer hung up; an abnormal 1006 means the socket was
      // severed underneath us — and "did the browser see a close at all" is the question
      // BUG-20260809-react-playground-partition-heal-never-reconnects turns on.
      console.log(
        `HermesConnection websocket closed code=${event.code} reason=${event.reason} wasClean=${event.wasClean}`
      );
      // A socket that was already replaced (the liveness watchdog closed it and its resume
      // has opened) must not dial again — that second dial is what orphans the first
      // resume. BUG-20260912-hidden-tab-leaks-a-gateway-socket-per-minute.
      if (closedSocketAction(webSocket, clientImpl.currentSocket) === "ignore") {
        console.log(
          "HermesConnection: closed socket is not the current one — already replaced, not redialling"
        );
        return;
      }
      clientImpl.reconnect();
    };

    // Recovery accounting: every connection after the first is a reconnect, and it opens
    // the recovery window that the next inbound frame closes (onMessageToClient).
    const stats = clientImpl.recoveryStats;
    stats.connections += 1;
    if (stats.connections > 1) {
      stats.reconnects += 1;
      stats.lastReconnectAt = Date.now();
    }

    // resend un ack'ed messages
    clientImpl.sentMessagesWaitingForAck.forEach((smr, _idempotentId) => {
      stats.resends += 1;
      this.sendSendMessageRequest(smr, false);
    });
  }

  // Bind the freshly-minted mailbox to this connection once ClientSessionStarted resolves
  // it. Returns `this` so the mint path can `resolve(pending.withMailbox(mbox))` inline.
  withMailbox(mailbox: CreateMailboxResponse): HermesConnection {
    this.mailbox = mailbox;
    return this;
  }

  onWebSocketTextMessage(message: string): void {
    const jsonObj = JSON.parse(message);
    const m2c = MessageToClient.fromJSON(jsonObj);
    this.onMessageToClient(m2c);
  }

  onWebSocketBinaryMessage(message: ArrayBuffer): void {
    const m2c = MessageToClient.decode(new Uint8Array(message));
    this.onMessageToClient(m2c);
  }

  onMessageToClient(m2c: MessageToClient): void {
    // Any inbound frame is proof of life — feed the liveness watchdog.
    this.clientImpl.lastInboundAt = Date.now();
    // First frame after a reconnect closes the recovery window (latched, so it measures
    // the FIRST message back rather than every later one).
    const stats = this.clientImpl.recoveryStats;
    if (stats.lastReconnectAt > 0) {
      stats.recoveryMs = Date.now() - stats.lastReconnectAt;
      stats.lastReconnectAt = 0;
    }
    // Real inbound = the session actually works. This — NOT WS-open — is what
    // clears the re-mint failure counter, so a socket that opens but never hears
    // back does not reset the backoff (the runaway-loop guard). Idempotent.
    if (!this.clientImpl.sessionConfirmed) {
      this.clientImpl.sessionConfirmed = true;
      this.clientImpl.unconfirmedRemints = 0;
      this.clientImpl.reconnectAttempt = 0;
    }
    if (m2c.clientSessionStarted !== undefined) {
      // The gateway answered a browserSession hello: the processrun is committed and the
      // mailbox minted+linked. Learn the server-owned processUid and resolve the deferred
      // mailbox promise, which unblocks newHermesConnection and the RPC path.
      const started = m2c.clientSessionStarted;
      this.clientImpl.browserProcessUid = started.processUid;
      const mbox: CreateMailboxResponse = {
        address: started.address,
        adminKey: started.adminKey,
        readerKey: started.readerKey,
        channels: started.channels,
      };
      this.clientImpl.mintedMailbox = mbox;
      console.log(
        `hermes client session started: mailbox ${started.address}, processrun ${started.processUid}`
      );
      // A RE-MINT: the mailbox subscriptions (rpc-inbox above all) are still keyed to the
      // session this one replaces. Re-key them to the new readerKey and re-send them on
      // THIS socket, or the new inbox has no reader and every RPC times out at 90s.
      // BUG-20260912-reminted-browser-session-never-subscribes-its-inbox.
      const previousReaderKey = this.clientImpl.previousReaderKey;
      this.clientImpl.previousReaderKey = undefined;
      if (previousReaderKey !== undefined && previousReaderKey !== mbox.readerKey) {
        const rekeyed = rekeyOwnMailboxSubscriptions(
          this.clientImpl.activeSubscriptions.values(),
          previousReaderKey,
          mbox.readerKey
        );
        if (rekeyed.length > 0) {
          this.sendMessageFromClient({
            subscribeRequest: { subscriptions: rekeyed },
          });
          console.log(
            `hermes client re-mint: re-subscribed ${rekeyed.length} mailbox subscription(s) from readerKey ${previousReaderKey} to ${mbox.readerKey}`
          );
        }
      }
      this.clientImpl.resolveMailbox(mbox);
    } else if (m2c.helloError !== undefined) {
      // A CODED refusal of the hello — the code is what decides recovery, per the enum's
      // own guidance (wsmessages.proto HelloErrorCode):
      //   UNAUTHENTICATED -> re-login (do NOT retry)
      //   MAILBOX_NOT_FOUND / NATS_STREAM_NOT_FOUND -> our resume target is gone; re-mint
      //   FORBIDDEN / INVALID_REQUEST -> permanent (surface, no auto-recovery)
      //   UNAVAILABLE / anything else -> transient; reject to the reconnect backoff
      const err = m2c.helloError;
      console.warn(
        `hermes client hello refused by gateway [${err.code}]: ${err.message}`
      );
      if (err.code === HelloErrorCode.UNAUTHENTICATED) {
        // STOP dialling and wait to be told there is a new token. The re-login
        // prompt below is fire-and-forget and already fires once
        // (__authExpiredInFlight); the prompt was never the loop, the redial was.
        this.clientImpl.authHalted = true;
        // Route to re-login via the mailbox promise's AuthExpiredError handling.
        this.clientImpl.rejectMailbox(new AuthExpiredError(401));
      } else if (
        err.code === HelloErrorCode.MAILBOX_NOT_FOUND ||
        err.code === HelloErrorCode.NATS_STREAM_NOT_FOUND
      ) {
        // A resume found nothing to reattach to. Two distinct causes, one recovery:
        //   MAILBOX_NOT_FOUND     — no mailbox record at all.
        //   NATS_STREAM_NOT_FOUND — the record is still there but its INBOX is gone: the
        //     ledger reaper deleted the NATS stream once the processrun went awol (a tab
        //     closed, or this laptop's lid shut past the AWOL window). The record
        //     outliving the stream is exactly why the gateway has to say which it means.
        //
        // Both are permanent for THIS session, so clear the stale keys and let the
        // reconnect mint a fresh one. Falling through to the generic branch instead would
        // reject without clearing them, and the next reconnect would resume onto the same
        // dead mailbox — the loop that let a woken browser fire 5,765 undeliverable
        // queries over 1h47m.
        // BUG-20260825-rpc-responder-floods-on-reclaimed-mailbox.
        this.clientImpl.mintedMailbox = undefined;
        this.clientImpl.rejectMailbox(
          new Error(`resume target gone: ${err.message}`)
        );
      } else {
        this.clientImpl.rejectMailbox(
          new Error(`hello refused [${err.code}]: ${err.message}`)
        );
      }
    } else if (m2c.notification !== undefined) {
      console.log(
        "hermes client received notification " + m2c.notification,
        m2c.notification
      );
    } else if (m2c.messageEnvelope !== undefined) {
      const me = m2c.messageEnvelope;
      if (me.messageBytes === undefined) {
        console.log(
          "hermes client received empty messageEnvelope",
          m2c.messageEnvelope
        );
      } else {
        const subscriptionId = me.serverEnvelope?.subscriptionId;
        if (subscriptionId) {
          const activeSub =
            this.clientImpl.activeSubscriptions.get(subscriptionId);
          if (activeSub) {
            // High-water rule (advanceResumePosition): store delivered+1 as the next
            // resume position, monotonically; drop stale replays at/below the mark
            // instead of handing the app a message it already saw.
            const verdict = advanceResumePosition(
              activeSub.protoRawSubscription.startSeq,
              me.serverEnvelope?.sequence
            );
            if (verdict.startSeq !== undefined) {
              activeSub.protoRawSubscription.startSeq = verdict.startSeq;
            }
            if (verdict.fresh) {
              activeSub.onMessageEvent(me);
            }
          }
        }
      }
    } else if (m2c.sendMessageResponse !== undefined) {
      const id = m2c.sendMessageResponse?.idempotentId;
      if (id) {
        retireOnSendMessageResponse(
          this.clientImpl.sentMessagesWaitingForAck,
          m2c.sendMessageResponse
        );
      }
      console.log(
        "hermes client received SendMessageResponse",
        m2c.sendMessageResponse
      );
    } else if (m2c.subscribeResponse !== undefined) {
      console.log(
        "hermes client received subscribeResponse",
        m2c.subscribeResponse
      );
    } else if (m2c.ping !== undefined) {
      this.webSocket.send(JSON.stringify({ pong: {} }));
    } else if (m2c.pong !== undefined) {
      console.log("hermes client received pong");
    }
  }

  sendMessageFromClient(mfc: MessageFromClient): void {
    console.log("sending websocket message", mfc);
    this.clientImpl.contentTypeHandler.sendMessageFromClient(
      mfc,
      this.webSocket
    );
  }

  addActiveSubscription(activeSub: ActiveSubscription): void {
    const listeners = this.clientImpl.activeSubscriptions.get(
      activeSub.subscriptionId
    );
    if (listeners) {
      // Subscription already exists - this can happen during React remounts
      // Just log a warning and return instead of throwing
      console.warn(
        `subscriptionId ${activeSub.subscriptionId} is already subscribed - ignoring duplicate subscription`
      );
      return;
    } else {
      this.clientImpl.activeSubscriptions.set(
        activeSub.subscriptionId,
        activeSub
      );
    }
  }

  cdcSubscribe<A>(
    cdcs: CdcSubscription,
    listener: (cdcEvent: ChangeDataCaptureEvent, a: A) => void
  ): void {
    const subscriptionId =
      "cdc-" + cdcs.tables.map((t) => t.database + "." + t.table).join("-");

    const protoCdcs: ChangeDataCaptureSubscription = {
      id: subscriptionId,
      state: "active",
      matchers: cdcs.tables.map((t) => ({
        database: t.database,
        table: t.table,
        primaryKeys: t.primaryKeys || [],
      })),
      startSeq: cdcs.startSeq || "all",
      endSeq: "tail",
    };

    this.sendMessageFromClient({
      subscribeRequest: { subscriptions: [{ changeDataCapture: protoCdcs }] },
    });

    function onMessage(msg: MessageEnvelope): void {
      const json = new TextDecoder().decode(msg.messageBytes);
      const cdcEvent = JSON.parse(json) as ChangeDataCaptureEvent;
      listener(cdcEvent, cdcEvent.data as A);
    }

    this.addActiveSubscription({
      subscriptionId: subscriptionId,
      protoRawSubscription: protoCdcs,
      onMessageEvent: onMessage,
      protoSubscription: { changeDataCapture: protoCdcs },
    });
  }

  channelMessageSubscribe(
    ms: MailboxSubscription,
    listener: (me: MessageEnvelope, msg: Message) => void
  ): void {
    this.rawChannelSubscribe<Uint8Array>(ms, (b) => b, (me, bytes) => {
      decodeMessage(bytes)
        .then((msg) => listener(me, msg))
        .catch((e) => console.error("error decoding message", e));
    });
  }

  channelSendReceiptSubscribe(
    ms: MailboxSubscription,
    listener: (me: MessageEnvelope, msg: SendReceipt) => void
  ): void {
    this.rawChannelSubscribe<Uint8Array>(ms, (b) => b, (me, bytes) => {
      decodeSendReceipt(bytes)
        .then((sr) => listener(me, sr))
        .catch((e) => console.error("error decoding send receipt", e));
    });
  }

  rawChannelSubscribe<A>(
    ms: MailboxSubscription,
    decoder: (bytes: Uint8Array) => A,
    listener: (me: MessageEnvelope, a: A) => void
  ): void {
    const subscriptionId = ms.id;
    if (!subscriptionId) {
      throw new Error("MailboxSubscription id is undefined");
    }

    function onMessage(msg: MessageEnvelope): void {
      if (msg.messageBytes === undefined) {
        console.error("MessageEnvelope.messageBytes is undefined");
        return;
      }
      const a = decoder(msg.messageBytes);
      listener(msg, a);
    }

    this.sendMessageFromClient({
      subscribeRequest: { subscriptions: [{ mailbox: ms }] },
    });

    this.addActiveSubscription({
      subscriptionId: subscriptionId,
      onMessageEvent: onMessage,
      protoRawSubscription: ms,
      protoSubscription: { mailbox: ms },
    });
  }

  continuumSubscribe(
    ns: ContinuumSubscription,
    listener: (me: MessageEnvelope) => void
  ): void {
    const subscriptionId = ns.id;
    if (!subscriptionId) {
      throw new Error("ContinuumSubscription id is undefined");
    }

    function onMessage(msg: MessageEnvelope): void {
      listener(msg);
    }

    this.sendMessageFromClient({
      subscribeRequest: { subscriptions: [{ continuum: ns }] },
    });

    this.addActiveSubscription({
      subscriptionId: subscriptionId,
      onMessageEvent: onMessage,
      protoRawSubscription: ns,
      protoSubscription: { continuum: ns },
    });
  }

  sendPing(): void {
    this.sendMessageFromClient({ ping: { payload: new Uint8Array(0) } });
  }

  sendSendMessageRequest(
    smr: SendMessageRequest,
    registerForAck: boolean
  ): void {
    // Normalise a hand-built message before it goes anywhere near the wire. A caller
    // that constructs one as an object literal (the conformance client does; so did
    // this file, twice) can leave a field undefined that the generated encoders treat
    // as "present but unknown": in JSON mode an undefined enum serialises as
    // "UNRECOGNIZED", the gateway rejects the frame, and the socket is closed. That is
    // what took every react-playground gauntlet cell to received=0 when RpcHeader
    // gained callKind (FEATURE-20260901-mesh-streaming-rpc). fromPartial fills every
    // absent field with its proto default, so a message missing a field is the same
    // bytes as one that set the zero value — which is the compatibility the wire
    // format promises and the generated code, on its own, does not keep.
    const normalised = SendMessageRequest.fromPartial(smr);
    if (registerForAck && normalised.idempotentId) {
      this.clientImpl.sentMessagesWaitingForAck.set(normalised.idempotentId, normalised);
    }
    this.sendMessageFromClient({ sendMessageRequest: normalised });
  }

  rawRpcCall(request: RawRpcRequest): Promise<Message> {
    // The mailbox is undefined only between socket-open and the ClientSessionStarted that
    // mints it. Callers reach a connection via withConn(), which awaits the RESOLVED
    // connection (mailbox in hand), so this never fires in practice — but assert rather
    // than send with an empty sender, which the gateway would reject anyway.
    const mailbox = this.mailbox;
    if (mailbox === undefined) {
      throw new Error("rawRpcCall before the mailbox was minted (no ClientSessionStarted)");
    }
    const emptyBytes = new Uint8Array(0);
    const correlationId =
      mailbox.address + "-" + this.clientImpl.correlationIdCounter++;
    const idempotentId = mailbox.address + correlationId;
    const smr: SendMessageRequest = {
      channel: Constants.rpcInboxChannelName,
      to: [request.to],
      idempotentId: idempotentId,
      message: {
        header: {
          rpcHeader: {
            correlationId: correlationId,
            endPoint: request.endPoint,
            frameType: RpcFrameType.Request,
            errorInfo: undefined,
            // The unary fast path. Zero values, so the frame this builds is
            // byte-identical to the one built before streaming existed — an unset
            // callKind IS SingleSingle to the server.
            callKind: RpcCallKind.UnspecifiedCK,
            frameSeq: "0",
            cancelReason: "",
          },
          sender: mailbox.address,
          contentType: request.contentType ?? ContentType.Binary,
          extraHeaders: request.headers ?? [],
          senderSequence: "0",
        },
        serverEnvelope: undefined,
        senderEnvelope: {
          created: String(Date.now()),
        },
        data: request.body !== undefined ? request.body : emptyBytes,
        // The payload rides inline in `data`; dataRef names an out-of-band blob
        // instead, and is empty when there isn't one.
        dataRef: "",
      },
    };

    const promise = new Promise<Message>((resolve, _reject) => {
      this.clientImpl.correlations.set(correlationId, resolve);
    });

    this.sendSendMessageRequest(smr, true);

    // Stall detector (the high-value verifyTransport trigger): if this request
    // hasn't come back within a short window, that stall is itself a signal the
    // transport may be broken (e.g. we migrated networks and the socket is
    // half-open). Proactively verify — if the IP moved, verifyTransport rebuilds
    // the WS in ~1-2s and the resub/retry rides the fresh socket, instead of this
    // request hanging ~75s for the liveness timeout. Non-invasive: we do NOT
    // reject the RPC here; a legitimately-slow response still resolves normally,
    // and verifyTransport is a cheap no-op when the IP is unchanged.
    // FEATURE-20260715-browser-fast-detection-of-migrated-wifi.
    let settled = false;
    promise.then(
      () => (settled = true),
      () => (settled = true)
    );
    setTimeout(() => {
      if (!settled) void this.clientImpl.verifyTransport();
    }, RPC_STALL_PROBE_MS);

    return promise;
  }

  // ── streaming RPC ──────────────────────────────────────────────────────────
  // FEATURE-20260901-mesh-streaming-rpc. Four call shapes, the product
  // (single | streaming request) x (single | streaming response); rawRpcCall above is
  // the first of them and does not change.
  //
  // The streaming type is ReadableStream, the Web Streams one — native in the browser
  // and in Node, pull-based like draining an inbox, with cancel() and error as
  // first-class lifecycle events that map onto the Go client's close(chan) / Err().
  // Not AsyncIterable (a protocol with no cancel), not an Observable (push-based, and
  // a dependency).
  //
  // These are RAW, at the same level as rawRpcCall: bodies in and Messages out. The
  // typed layer belongs in each service client, where the endpoint's own oneof lives.

  /**
   * Single request, streaming response — the common shape: a query, a listing, a
   * token stream.
   */
  rawRpcCallStreamingResponse(request: RawRpcRequest): RpcStreamCall {
    return this.openRpcStream(
      RpcCallKind.SingleReqStreamingResp,
      request,
      undefined
    );
  }

  /**
   * Streaming request, streaming response. The two directions are independent:
   * either side may finish first.
   */
  rawRpcCallStreaming(
    requests: ReadableStream<Uint8Array>,
    request: RawRpcRequest
  ): RpcStreamCall {
    return this.openRpcStream(
      RpcCallKind.StreamingReqStreamingResp,
      request,
      requests
    );
  }

  /**
   * Streaming request, single response — an upload, a batch submit. Resolves with the
   * one answer, so it reads like the unary call it resembles.
   */
  async rawRpcCallStreamingRequest(
    requests: ReadableStream<Uint8Array>,
    request: RawRpcRequest
  ): Promise<Message> {
    const call = this.openRpcStream(
      RpcCallKind.StreamingReqSingleResp,
      request,
      requests
    );
    const reader = call.responses.getReader();
    // A single-response call puts its answer in the terminal SuccessResponse, so the
    // stream carries exactly one element and then closes.
    const first = await reader.read();
    if (first.done) {
      throw new Error(
        `${request.endPoint} ended without a response`
      );
    }
    await reader.cancel().catch(() => undefined);
    return first.value;
  }

  /**
   * The shared body of all three: open the call, wire the response stream, and pump
   * the request stream when there is one.
   */
  private openRpcStream(
    callKind: RpcCallKind,
    request: RawRpcRequest,
    requests: ReadableStream<Uint8Array> | undefined
  ): RpcStreamCall {
    const mailbox = this.mailbox;
    if (mailbox === undefined) {
      throw new Error(
        "streaming rpc before the mailbox was minted (no ClientSessionStarted)"
      );
    }
    const correlationId =
      mailbox.address + "-" + this.clientImpl.correlationIdCounter++;

    // Request-side sequence. The Request frame IS element 1 of the request stream, so
    // the first StreamData that follows is element 2 — matching what the server
    // expects, and the off-by-one that failed the Go conformance run until it was
    // fixed on both sides.
    let sendSeq = 1;
    // Response-side: the next frameSeq this call expects to see.
    let expectSeq = 1;
    let finished = false;

    const sendFrame = (
      frameType: RpcFrameType,
      opts: {
        seq?: number;
        body?: Uint8Array;
        kind?: RpcCallKind;
        cancelReason?: string;
      } = {}
    ): void => {
      const smr: SendMessageRequest = {
        channel: Constants.rpcInboxChannelName,
        to: [request.to],
        // A fresh idempotentId per FRAME: the stream's dedup window is keyed on it, so
        // reusing one would make the stream swallow this call's own later frames.
        idempotentId: mailbox.address + correlationId + "-" + frameType + "-" + (opts.seq ?? 0) + "-" + Date.now(),
        message: {
          header: {
            rpcHeader: {
              correlationId,
              endPoint: request.endPoint,
              frameType,
              callKind: opts.kind ?? RpcCallKind.UnspecifiedCK,
              frameSeq: String(opts.seq ?? 0),
              cancelReason: opts.cancelReason ?? "",
              errorInfo: undefined,
            },
            sender: mailbox.address,
            contentType: request.contentType ?? ContentType.Binary,
            extraHeaders: request.headers ?? [],
            senderSequence: "0",
          },
          serverEnvelope: undefined,
          senderEnvelope: { created: String(Date.now()) },
          data: opts.body ?? new Uint8Array(0),
          dataRef: "",
        },
      };
      this.sendSendMessageRequest(smr, true);
    };

    let idleTimer: ReturnType<typeof setTimeout> | undefined;
    let controller: ReadableStreamDefaultController<Message> | undefined;

    const finish = (err?: Error): void => {
      if (finished) return;
      finished = true;
      if (idleTimer !== undefined) clearTimeout(idleTimer);
      this.clientImpl.correlations.delete(correlationId);
      if (controller === undefined) return;
      if (err) {
        controller.error(err);
      } else {
        controller.close();
      }
    };

    const cancel = (reason?: string): void => {
      if (finished) return;
      sendFrame(RpcFrameType.Cancel, {
        cancelReason: reason ?? "cancelled by the caller",
      });
      finish(new Error(`${request.endPoint} cancelled: ${reason ?? "cancelled by the caller"}`));
    };

    const armIdle = (): void => {
      if (idleTimer !== undefined) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        // Silence for the whole deadline in both directions: the peer is gone or
        // wedged, and an answer is not coming. Without this the call waits forever.
        sendFrame(RpcFrameType.Cancel, { cancelReason: "idle deadline exceeded" });
        finish(
          new Error(
            `${request.endPoint}: no frame from ${request.to} for ${RPC_STREAM_IDLE_MS}ms`
          )
        );
      }, RPC_STREAM_IDLE_MS);
    };

    const responses = new ReadableStream<Message>({
      start: (c) => {
        controller = c;
      },
      // A viewer that stops reading should stop the SERVER too, not just stop
      // listening — which is the whole reason Cancel is a frame.
      cancel: (reason) => {
        if (finished) return;
        sendFrame(RpcFrameType.Cancel, {
          cancelReason: typeof reason === "string" ? reason : "reader cancelled",
        });
        finished = true;
        if (idleTimer !== undefined) clearTimeout(idleTimer);
        this.clientImpl.correlations.delete(correlationId);
      },
    });

    this.clientImpl.correlations.set(correlationId, (msg: Message): boolean => {
      if (finished) return true;
      armIdle();
      const rpcHeader = msg.header?.rpcHeader;
      const action = classifyStreamFrame(
        rpcHeader?.frameType,
        rpcHeader?.frameSeq,
        rpcHeader?.errorInfo?.message,
        expectSeq
      );
      switch (action.kind) {
        case "deliver":
          expectSeq = action.nextExpect;
          controller?.enqueue(msg);
          return false;
        case "duplicate":
        case "ignore":
          return false;
        case "gap":
          finish(
            new Error(
              `gap in the response stream of ${request.endPoint}: got frame ${action.got}, expected ${action.expected}`
            )
          );
          return true;
        case "single":
          controller?.enqueue(msg);
          finish();
          return true;
        case "end":
          finish();
          return true;
        case "error":
          finish(new Error(`${request.endPoint}: ${action.message}`));
          return true;
      }
    });

    armIdle();

    if (requests === undefined) {
      sendFrame(RpcFrameType.Request, {
        body: request.body,
        kind: callKind,
      });
    } else {
      // A streaming request: the Request frame carries element 1, so the stream has to
      // produce something before the call can open at all.
      void (async () => {
        const reader = requests.getReader();
        try {
          const first = await reader.read();
          if (first.done) {
            finish(
              new Error(
                `the request stream of ${request.endPoint} was closed before it produced anything`
              )
            );
            return;
          }
          sendFrame(RpcFrameType.Request, { body: first.value, kind: callKind });
          for (;;) {
            const next = await reader.read();
            if (finished) return;
            if (next.done) {
              sendFrame(RpcFrameType.StreamEnd, { seq: sendSeq + 1 });
              return;
            }
            sendSeq++;
            sendFrame(RpcFrameType.StreamData, {
              seq: sendSeq,
              body: next.value,
            });
          }
        } catch (e) {
          if (!finished) {
            cancel("the request stream failed: " + String(e));
          }
        } finally {
          reader.releaseLock();
        }
      })();
    }

    return { responses, cancel };
  }

  rpcObserverSubscribe(
    readerKey: string,
    listener: (correlation: RpcRequestResponse) => void
  ): void {
    console.log("rpcObserverSubscribe", readerKey);

    const correlations = new Map<string, RpcRequestResponse>();

    const msInbox: MailboxSubscription = {
      id: "rpc-inbox-" + readerKey,
      state: "active",
      readerKey: readerKey,
      channel: "rpc-inbox",
      startSeq: "first",
      endSeq: "tail",
    };
    this.channelMessageSubscribe(msInbox, (me, msg) => {
      const correlationId = msg.header?.rpcHeader?.correlationId;
      if (correlationId) {
        let correlation = correlations.get(correlationId);
        if (!correlation) {
          correlation = new RpcRequestResponse(correlationId);
          correlations.set(correlationId, correlation);
        }
        correlation.inboxEnvelope = me;
        correlation.inboxMessage = msg;
        listener(correlation);
      }
    });

    const msSent: MailboxSubscription = {
      id: "rpc-sent-" + readerKey,
      state: "active",
      readerKey: readerKey,
      channel: "rpc-sent",
      startSeq: "first",
      endSeq: "tail",
    };
    this.channelSendReceiptSubscribe(msSent, (me, sr) => {
      const msg = sr.request?.message;
      const correlationId =
        sr.request?.message?.header?.rpcHeader?.correlationId;
      if (correlationId !== undefined) {
        let correlation = correlations.get(correlationId);
        if (correlation === undefined) {
          correlation = new RpcRequestResponse(correlationId);
          correlations.set(correlationId, correlation);
        }
        correlation.sentMessage = msg;
        correlation.sendReceiptEnvelope = me;
        correlation.sendReceipt = sr;
        listener(correlation);
      }
    });
  }
}

/**
 * What one inbound frame of a streaming call means. Pure, and exported, because this
 * is the part of the streaming contract worth pinning in a test: the ORDER rules
 * (deliver / duplicate / gap) and the fact that a call ends on exactly one terminal.
 * FEATURE-20260901-mesh-streaming-rpc.
 */
export type StreamFrameAction =
  | { kind: "deliver"; nextExpect: number }
  | { kind: "duplicate" }
  | { kind: "gap"; expected: number; got: number }
  | { kind: "single" }
  | { kind: "end" }
  | { kind: "error"; message: string }
  | { kind: "ignore" };

/**
 * classifyStreamFrame applies the receive-side half of the streaming contract.
 *
 * Duplicates are LEGAL — delivery is at-least-once, so a replayed frame is expected
 * input and is dropped, not an error. A gap is the opposite: one publisher on one
 * connection into one ordered stream cannot reorder, so a sequence AHEAD of the mark
 * means a frame was genuinely lost, and the call fails rather than handing the app a
 * result with a hole in it.
 */
export function classifyStreamFrame(
  frameType: RpcFrameType | undefined,
  frameSeq: string | number | undefined,
  errorMessage: string | undefined,
  expectSeq: number
): StreamFrameAction {
  switch (frameType) {
    case RpcFrameType.StreamData: {
      const seq = Number(frameSeq ?? 0);
      if (seq < expectSeq) return { kind: "duplicate" };
      if (seq > expectSeq) return { kind: "gap", expected: expectSeq, got: seq };
      return { kind: "deliver", nextExpect: expectSeq + 1 };
    }
    case RpcFrameType.SuccessResponse:
      // A single-response call: the payload IS the terminal frame.
      return { kind: "single" };
    case RpcFrameType.StreamEnd:
      return { kind: "end" };
    case RpcFrameType.ErrorResponse:
      return { kind: "error", message: errorMessage ?? "the call failed with no message" };
    case RpcFrameType.Heartbeat:
      // Proof of life and nothing else; it has already reset the idle deadline.
      return { kind: "ignore" };
    default:
      return { kind: "ignore" };
  }
}

/**
 * A streaming call in flight. The library owns `responses`: it enqueues every element
 * and settles the stream exactly once, on the call's one terminal frame — closed after
 * StreamEnd, errored after an ErrorResponse, a cancel, or the idle deadline. Cancel is
 * safe to call any number of times, including after the call has ended.
 */
export interface RpcStreamCall {
  responses: ReadableStream<Message>;
  cancel: (reason?: string) => void;
}

interface RawRpcRequest {
  to: MailboxAddress;
  endPoint: string;
  body?: Uint8Array;
  contentType?: ContentType;
  headers?: KeyValPair[];
  state?: Uint8Array;
  // Hard ceiling for a unary rawRpcCall, in ms; RPC_TIMEOUT_MS when unset (see
  // rpcTimeoutMs). Streaming calls ignore it: they are bounded by RPC_STREAM_IDLE_MS.
  timeoutMs?: number;
}

// interface RawSubscription<A> {
//   mailboxSubscription: MailboxSubscription;
//   decoder: (bytes: Uint8Array)=>A;
//   listener: (me: MessageEnvelope, a: A)=>void;
//   rawListener: MessageEnvelopeListener;
// }

type RawSubscription =
  | MailboxSubscription
  | ChangeDataCaptureSubscription
  | ContinuumSubscription;
export interface ActiveSubscription {
  subscriptionId: string;
  protoRawSubscription: RawSubscription;
  onMessageEvent(messageEnvelope: MessageEnvelope): void;
  protoSubscription: Subscription;
}

class HermesClientImpl implements HermesClient {
  currentConn: Promise<HermesConnection>;
  contentTypeHandler: ContentTypeHandler;
  wsUrl: string;
  // Resolves with the mailbox keys the GATEWAY mints in response to
  // ClientHello{browserSession}. The browser no longer mints over HTTP: the mailbox is
  // minted by the WS handshake itself (ClientSessionStarted), so this promise is now
  // resolved from that inbound frame rather than a pre-socket fetch. Reassigned to a fresh
  // deferred on each re-mint. Assigned by mintBrowserSession() from the constructor before
  // any read (TS can't see through the method call — hence the definite-assignment `!`).
  mailboxResponseP!: Promise<CreateMailboxResponse>;
  // resolve/reject handles for the CURRENT mint's mailboxResponseP, so the handshake
  // response (ClientSessionStarted) or a HelloError can settle it from onMessageToClient
  // (which lives on HermesConnection, hence not private).
  resolveMailbox!: (mbox: CreateMailboxResponse) => void;
  rejectMailbox!: (err: unknown) => void;
  // The keys once minted/resumed. undefined until the first ClientSessionStarted; a re-mint
  // clears it so the next socket sends browserSession (mint) not resumeSession (reattach).
  mintedMailbox: CreateMailboxResponse | undefined = undefined;
  // The browser session's processrun uid. MINTED BY THE GATEWAY now (server-owned for a
  // browser), so it is learned from ClientSessionStarted rather than generated client-side.
  browserProcessUid: string = "";
  correlationIdCounter: number = 0;
  correlations: Map<string, MessageListener> = new Map();
  activeSubscriptions: Map<string, ActiveSubscription> = new Map();
  // The readerKey of the session a re-mint is replacing, held from mintBrowserSession()
  // until ClientSessionStarted names the new one, so the mailbox subscriptions keyed to
  // it can be re-keyed and re-sent (rekeyOwnMailboxSubscriptions) and kept OFF the mint
  // hello (helloSubscriptions). undefined outside that window.
  // BUG-20260912-reminted-browser-session-never-subscribes-its-inbox.
  previousReaderKey: string | undefined = undefined;
  sentMessagesWaitingForAck: Map<string, SendMessageRequest> = new Map();
  rootUrl: string;

  // The token supplied at construction. Only a FALLBACK — read `authToken` instead.
  private constructedAuthToken?: string;

  /**
   * The auth token to present, resolved LAZILY on every read.
   *
   * It used to be a plain field captured in the constructor, and that was a bug with
   * teeth: GlobalClient.get is memoized (bottom of this file), so the client is built
   * exactly once per page and its token frozen forever. Anything that touched
   * GlobalClient before /auth/handoff persisted the token — any module-scope reference,
   * any component mounting during the login round-trip — produced a client permanently
   * holding "". The constructor immediately opens the socket, so there was no recovery:
   * every ClientHello went out unauthenticated, the gateway refused it
   * (activews.go GetAuthenticatedUserUid == "" -> "cannot start browser session: not
   * authenticated"), and the client mapped that refusal to a hardcoded
   * AuthExpiredError(401), whose handler then CLEARED the stored token — so the next
   * attempt was guaranteed to fail the same way. Observed live as
   * GlobalClient.get().authToken === "" while a valid 24h session sat in localStorage.
   *
   * Reading through AuthTokenManager on each access fixes the whole family at once:
   * the initial hello, every reconnect hello, and the mailbox mints below. It also
   * makes impersonation work correctly, since getToken() honours the per-tab override
   * that may be set long after this client was constructed.
   *
   * See BUG-20260726-ws-createmailbox-401-wipes-valid-token.
   */
  get authToken(): string | undefined {
    try {
      return AuthTokenManager.getInstance().getToken() ?? this.constructedAuthToken;
    } catch {
      // getInstance() throws if init() has not run yet (headless/test contexts).
      return this.constructedAuthToken;
    }
  }

  // --- reconnect / liveness state ---
  // Timestamp (ms) of the last message received from the server. Updated on any
  // inbound frame; the liveness watchdog uses it to detect a dead connection
  // even when no FIN/close arrives (half-open socket).
  lastInboundAt: number = Date.now();
  // Wall clock of the last liveness tick. The gap between consecutive ticks is the
  // only place this client can observe that the MACHINE was suspended — see
  // suspensionAction. Seeded at construction so the first tick measures one period,
  // not "since the epoch".
  lastLivenessTickAt: number = Date.now();
  // True while a (re)connect attempt is in flight, so we don't stack attempts
  // when onclose + the watchdog both fire.
  reconnecting: boolean = false;
  // The socket most recently OPENED — the one currentConn is (or is about to be) built on.
  // onclose compares against it (closedSocketAction): a close on any other socket is the
  // tail of a replacement that already happened, and redialling for it leaks the
  // replacement. Set synchronously in onopen, before the connection is wired, so the
  // comparison never sees a promise.
  currentSocket: WebSocket | undefined = undefined;
  // HALTED on an UNAUTHENTICATED hello refusal, and released only by a fresh
  // token (AuthTokenManager.setToken -> resumeFromAuthHalt). Redialling cannot
  // help: the gateway refuses the hello because there is no valid token, and no
  // number of retries mints one — only a human logging back in does. Backoff
  // would be the wrong shape here; it keeps a loop that should not exist and
  // merely hammers politely. See
  // BUG-20260910-ws-onclose-redials-through-unauthenticated.
  authHalted: boolean = false;
  reconnectAttempt: number = 0;
  // A session is CONFIRMED only once we've received a real inbound frame from the
  // server since the last mint — NOT when the WS merely opened. In the runaway
  // re-mint loop (BUG-20260719-browser-remint-loop-unbounded-dead-mailboxes) the
  // socket DID open each cycle, so gating on open let the loop reset its backoff
  // forever. Set true in onMessageToClient (the real-inbound choke point); reset
  // false on each mint.
  sessionConfirmed: boolean = false;
  // Consecutive re-mints that never confirmed a session. Past MAX_UNCONFIRMED_REMINTS
  // we STOP minting fresh mailboxes (each one is dead) and back off, retrying the
  // SAME mailbox — a session that never got going can't be fixed by re-minting.
  unconfirmedRemints: number = 0;

  // RECOVERY COUNTERS — invariant 6 of godev docs/mesh-client/client-invariants.md: the
  // numbers only the client can see, surfaced so the conformance REPORT verb can answer
  // them instead of -1 ("not tracked"). Without them a perfect post-recovery run is
  // indistinguishable from an outage that never landed — the INERT verdict
  // (BUG-20260801-wsconform-browser-row-kill-inert) — because correct recovery is now
  // TRACELESS at the payload level (delivered+1 resume, no dups).
  // -1 recoveryMs means the window never closed; lastReconnectAt latches on the first
  // inbound frame after a reconnect so it measures the FIRST message back.
  recoveryStats = {
    connections: 0, // constructor count; the first is bootstrap, not a reconnect
    reconnects: 0,
    resends: 0,
    remints: 0,
    recoveryMs: -1,
    lastReconnectAt: 0,
  };
  // The client IP the SERVER last reported for us (via /api/whoami). A browser
  // cannot read its own public IP, so this is how we detect a network migration
  // (WiFi switch): if a fresh probe differs, we moved and must rebuild the WS
  // immediately. undefined until the first probe.
  // FEATURE-20260715-browser-fast-detection-of-migrated-wifi.
  lastKnownIp: string | undefined = undefined;
  // Guards verifyTransport() so overlapping triggers (wake + a stalled request)
  // don't fire concurrent probes.
  private verifyingTransport: boolean = false;

  constructor(
    rootUrl: string,
    contentTypeHandler: ContentTypeHandler,
    authToken?: string
  ) {
    this.rootUrl = rootUrl;
    this.contentTypeHandler = contentTypeHandler;
    // Fallback only; `authToken` (the getter above) prefers the LIVE token.
    this.constructedAuthToken = authToken;

    // Browser session identity, minted client-side (daemon pattern): the mailbox is
    // created WITH this processRunUid so the mailbox<->processrun link is made at
    // creation. The gateway builds the processrun from the matching BrowserSessionStart.
    this.mintBrowserSession();

    const tempWsUrl = new URL(rootUrl);
    tempWsUrl.protocol = tempWsUrl.protocol.replace("http", "ws");

    tempWsUrl.pathname = contentTypeHandler.webSocketUrlPath;

    this.wsUrl = tempWsUrl.toString();

    // A fresh token is the one event that can release an auth halt: the halted
    // client is not talking to the server, so nothing else can tell it to try
    // again. Dial ONCE on release — not a resumed retry ladder.
    onAuthTokenSet(() => this.resumeFromAuthHalt());

    this.currentConn = this.newHermesConnection();
  }

  // Release the UNAUTHENTICATED halt and make exactly one dial. A no-op unless
  // the client is actually halted, so an ordinary token refresh does not stack a
  // connection on top of a healthy one.
  resumeFromAuthHalt() {
    if (!this.authHalted) {
      return;
    }
    console.log("hermes: new auth token stored — resuming the halted session");
    this.authHalted = false;
    // The halt left these where the refusal found them; a new token is a new
    // session, so the next dial must not be judged by the failed one's counters.
    this.reconnectAttempt = 0;
    this.unconfirmedRemints = 0;
    this.reconnect();
  }

  sendPongResponse(
    mbox: CreateMailboxResponse,
    pingMsg: Message,
    endPoint: string
  ): void {
    const correlationId = pingMsg.header?.rpcHeader?.correlationId;
    const sender = pingMsg?.header?.sender;
    const contentType =
      pingMsg?.header?.contentType ?? ContentType.UnspecifiedCT;
    if (correlationId !== undefined && sender !== undefined) {
      let ping: Ping = { payload: new Uint8Array(0) };
      if (pingMsg.data !== undefined) {
        if (contentType === ContentType.Json) {
          ping = Ping.fromJSON(pingMsg.data);
        } else {
          ping = Ping.decode(pingMsg.data);
        }
      }
      const pong: Pong = { payload: ping.payload };
      let data: Uint8Array;
      if (contentType === ContentType.Json) {
        data = new TextEncoder().encode(JSON.stringify(Pong.toJSON(pong)));
      } else {
        data = Pong.encode(pong).finish();
      }
      const idempotentId = mbox.address + correlationId;
      const smr: SendMessageRequest = {
        channel: Constants.rpcInboxChannelName,
        to: [sender as MailboxAddress],
        idempotentId: idempotentId,
        message: {
          header: {
            rpcHeader: {
              correlationId: correlationId,
              endPoint: endPoint,
              frameType: RpcFrameType.SuccessResponse,
              errorInfo: undefined,
              callKind: RpcCallKind.UnspecifiedCK,
              frameSeq: "0",
              cancelReason: "",
            },
            sender: mbox.address,
            contentType: pingMsg?.header?.contentType ?? ContentType.Binary,
            senderSequence: "0",
            extraHeaders: [],
          },
          serverEnvelope: undefined,
          senderEnvelope: {
            created: String(Date.now()),
          },
          data: data,
          dataRef: "",
        },
      };
      this.withConn((conn) => {
        conn.sendSendMessageRequest(smr, true);
      });
    } else {
      console.log("ignoring ping no correlation id", pingMsg);
    }
  }

  // Trigger a reconnect. Idempotent: if an attempt is already in flight (e.g.
  // both onclose and the liveness watchdog fire), this is a no-op.
  //
  // mintBrowserSession (re)mints this session's identity. The mint is now done BY THE
  // GATEWAY over the WebSocket: the next socket sends ClientHello{browserSession}, and the
  // gateway writes the processrun (server-owned processUid) and mints the mailbox linked to
  // it before answering — the processrun-first guarantee, moved onto the handshake
  // (TASK-20260723-create-mailbox-hard-check-processrun-exists). This method no longer talks
  // HTTP; it just resets a DEFERRED mailbox promise that the ClientSessionStarted frame (or a
  // HelloError) resolves. tabUid is NOT reminted — it is the immutable per-tab parent, so a
  // re-mint is recognizably "same tab, new mailbox". Called at construction and on wake re-mint.
  mintBrowserSession() {
    // Remember the session being replaced: its readerKey is what the live mailbox
    // subscriptions are keyed to, and ClientSessionStarted re-keys them from it.
    if (this.mintedMailbox !== undefined) {
      this.previousReaderKey = this.mintedMailbox.readerKey;
    }
    // Clear the keys so the next socket sends browserSession (mint), not resumeSession.
    this.mintedMailbox = undefined;
    this.browserProcessUid = "";

    // A fresh deferred, settled by the handshake response in onMessageToClient.
    this.mailboxResponseP = new Promise<CreateMailboxResponse>((resolve, reject) => {
      this.resolveMailbox = resolve;
      this.rejectMailbox = reject;
    });

    // A mint that fails on EXPIRED AUTH must route to re-login, not retry: the
    // reconnect loop awaits mailboxResponseP and would otherwise back off forever
    // (or surface an unhandled rejection). Catch it here — the one place the
    // promise is created — so every awaiter is covered. A transient/network
    // failure is a normal Error and is left to the existing reconnect backoff.
    // Attach a no-op catch to the shared promise too, so awaiters that handle
    // their own errors don't also log an unhandled rejection.
    this.mailboxResponseP.catch((err) => {
      if (err instanceof AuthExpiredError) {
        // Fire-and-forget: triggerAuthExpired is async only because it confirms
        // with the server before clearing the token. Nothing here awaits the
        // outcome — it drives the re-login UI on its own.
        void triggerAuthExpired();
      }
    });
    // A mint that SUCCEEDS clears the auth-expired latch, so a future expiry
    // (much later in this tab's life) can trigger the flow again.
    this.mailboxResponseP.then(() => {
      __authExpiredInFlight = false;
    }, () => {});
  }

  // remint=true mints a fresh session first (new processUid + mailbox). Used when the
  // connection was silent longer than the browser AWOL window — the old processrun is
  // (or is about to be) reclaimed, so resuming it would re-announce a dead uid. For a
  // brief blip, remint=false resumes the same session (cheaper, no churn).
  reconnect(remint: boolean = false) {
    // onclose fires on the socket the gateway closed BEHIND a refusal and has no
    // idea why it closed, so without the auth-halted arm the
    // "UNAUTHENTICATED -> do NOT retry" policy in onMessageToClient governs only
    // the mailbox promise while the SOCKET redials anyway — measured at a flat
    // ~400ms, ~22k refused hellos in 6h from one logged-out tab.
    if (dialDecision(this.reconnecting, this.authHalted) !== "dial") {
      return;
    }
    this.reconnecting = true;

    // Runaway-loop guard (BUG-20260719): re-minting can't fix a session that never
    // got going, so past MAX_UNCONFIRMED_REMINTS consecutive un-confirmed re-mints
    // we stop minting FRESH mailboxes (each is a dead mailbox+stream) and retry the
    // SAME mailbox with a backoff. A real inbound (onMessageToClient) clears the
    // counter, so normal AWOL-recovery re-minting resumes once the session works.
    const decision = remintDecision(remint, this.unconfirmedRemints);

    if (decision === "mint") {
      console.log(
        "hermes: silent past the browser AWOL window — re-minting the session (same tab, new mailbox)"
      );
      this.sessionConfirmed = false;
      this.unconfirmedRemints += 1;
      this.recoveryStats.remints += 1;
      this.mintBrowserSession();
    } else if (decision === "backoff") {
      // Delay ceiling grows with the overshoot, capped at 5 min, so a stuck tab probes
      // occasionally instead of minting a dead mailbox every ~65s. Full-jittered: stuck
      // tabs are stuck together (same deploy, same outage), and unjittered they probe
      // together forever.
      const overshoot = this.unconfirmedRemints - MAX_UNCONFIRMED_REMINTS;
      const delayMs = remintBackoffDelayMs(overshoot);
      this.unconfirmedRemints += 1;
      console.warn(
        `hermes: ${this.unconfirmedRemints} re-mints without a confirmed session — ` +
          `NOT minting another (would be a dead mailbox); retrying the same mailbox in ${delayMs}ms`
      );
      setTimeout(() => {
        this.reconnecting = false;
        this.reconnect(false);
      }, delayMs);
      return;
    }
    this.currentConn = this.newHermesConnection();
  }

  // Establish a websocket, retrying with backoff until it opens. A server
  // restart can take several seconds and may not send a FIN, so we keep trying
  // rather than hanging on a single onopen that never fires.
  //
  // The FIRST FRAME is now a ClientHello, in one of two variants:
  //   - no keys yet (fresh mint / re-mint) -> browserSession: the gateway writes the
  //     processrun and MINTS the mailbox, returning its keys in ClientSessionStarted.
  //   - keys in hand (reconnect against the same mailbox) -> resumeSession: the gateway
  //     reattaches by readerKey and does NOT mint.
  // Either way the subscriptions ride ON the hello, so there is no separate FirstMessage.
  // This is glen's rebuild-WS-independent-of-re-mint invariant made explicit in the wire:
  // a rebuilt socket resumes; only a past-AWOL re-mint clears the keys and mints anew.
  newHermesConnection(): Promise<HermesConnection> {
    // The dial ladder — including the watchdog for a path that swallows the connect — lives
    // in openSocketWithRetry. This method owns only what happens once a socket is OPEN.
    return openSocketWithRetry<WebSocket>(
      () => {
        const webSocket = new WebSocket(this.wsUrl);
        webSocket.binaryType = "arraybuffer";
        return webSocket;
      },
      () => {
        this.reconnectAttempt += 1;
        // backoff ceiling: 0.5s, 1s, 2s, … capped at 10s — full-jittered under it
        const delay = dialBackoffDelayMs(this.reconnectAttempt);
        console.log(
          `hermes websocket connect failed, retrying in ${delay}ms (attempt ${this.reconnectAttempt})`
        );
        return delay;
      }
    ).then(
      (webSocket) =>
        new Promise<HermesConnection>((resolve) => {
          // The server's subscriptions are in-memory pubsub and are gone after
          // a restart — re-send every active subscription. Each carries its
          // last-seen startSeq (updated on delivery) so we resume without gaps.
          // A subscription still keyed to a session a re-mint replaced stays off the
          // hello; it is re-keyed and re-sent once the new session is named.
          const resubscriptions = helloSubscriptions(
            this.activeSubscriptions.values(),
            this.previousReaderKey
          );

          const existing = this.mintedMailbox;
          let clientHello: ClientHello;
          if (existing === undefined) {
            // MINT: the gateway builds the processrun (server-owned processUid) and the
            // mailbox from this. ip/userAgent are server-polyfilled; a client value is
            // ignored. authToken is the OAuth token (no SSH challenge in a browser).
            console.log("hermes client websocket opened, sending browserSession hello");
            clientHello = {
              browserSession: {
                tabUid: tabUid,
                mainName: browserMainName,
                startedAt: undefined,
                url: globalThis.location?.href ?? "",
                referer: globalThis.document?.referrer ?? "",
                locale: globalThis.navigator?.language ?? "",
                timezone:
                  Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone ?? "",
                authToken: this.authToken ?? "",
                // The bundle's build identity (vite-stamped) — how a browser
                // processrun gets codebase/version/commit in the registry.
                buildInfo: bundleBuildInfo(),
                ip: "",
                userAgent: "",
                extraData: undefined,
              },
              subscriptions: resubscriptions,
            };
          } else {
            // RESUME: reattach to the mailbox we already hold. No mint.
            console.log("hermes client websocket opened, sending resumeSession hello");
            clientHello = {
              resumeSession: {
                readerKey: existing.readerKey,
                authToken: this.authToken ?? "",
              },
              subscriptions: resubscriptions,
            };
          }

          this.contentTypeHandler.sendMessageFromClient(
            { clientHello },
            webSocket
          );

          // Socket open: clear the reconnecting guard and start the liveness
          // clock. Do NOT reset reconnectAttempt / the re-mint counter here — a
          // socket that opens is not yet a working session. Those reset only on
          // real inbound (onMessageToClient), so a socket that opens but never
          // hears back cannot defeat the backoff (the runaway-loop guard).
          this.reconnecting = false;
          this.lastInboundAt = Date.now();
          // One socket, always: adopt the new socket FIRST (so the old one's onclose reads
          // as a replaced socket and does not redial), then close whatever it replaced.
          const previousSocket = this.currentSocket;
          this.currentSocket = webSocket;
          if (retirePreviousSocket(previousSocket, webSocket)) {
            console.log(
              "hermes websocket: new socket open — closed the socket it replaced"
            );
          }

          // Wire the connection NOW so its onmessage is live to receive the handshake
          // response (ClientSessionStarted) on this very socket. On a resume we already
          // hold the keys; on a mint we await the deferred that ClientSessionStarted
          // resolves, then hand back a connection bound to the freshly-minted mailbox.
          if (existing !== undefined) {
            resolve(new HermesConnection(this, existing, webSocket));
          } else {
            const pending = new HermesConnection(this, undefined, webSocket);
            this.mailboxResponseP.then(
              (mbox) => resolve(pending.withMailbox(mbox)),
              () => {
                // Mint rejected (e.g. HelloError / auth). Tear the socket down; the
                // reconnect path (auth-expired -> re-login, else backoff) owns recovery.
                try {
                  webSocket.close();
                } catch {
                  /* ignore */
                }
              }
            );
          }
        })
    );
  }

  // Liveness watchdog: if we haven't heard anything from the server within the
  // deadline, the connection is presumed dead (half-open, no FIN). Force-close
  // it so onclose fires and we reconnect. Called periodically alongside ping.
  checkLiveness(deadlineMs: number) {
    // The tick is recorded BEFORE the reconnecting guard below: an early return that
    // skipped it would leave a stale mark and manufacture a false "we were suspended"
    // gap on the next tick.
    const nowMs = Date.now();
    const wallGap = nowMs - this.lastLivenessTickAt;
    this.lastLivenessTickAt = nowMs;

    // MAILBOX liveness, not socket liveness. A suspended machine wakes with a healthy
    // socket and a dead mailbox; every check below this one would call that fine. See
    // suspensionAction for the measurement and why re-minting is the right default.
    if (this.reconnecting) return;
    if (suspensionAction(wallGap) === "remint") {
      console.log(
        `hermes: liveness timer gap of ${wallGap}ms (>= ${browserAwolWindowMs}ms) — machine was suspended, ` +
          `the mailbox is past the server's AWOL window; re-minting before sending anything`
      );
      this.currentConn.then((conn) => {
        try {
          conn.webSocket.close();
        } catch {
          /* ignore */
        }
        this.reconnect(true);
      });
      return;
    }

    const silentFor = nowMs - this.lastInboundAt;
    // Judge only the silence this tick could have observed: a throttled hidden tab
    // wakes once a minute, and the ~58s between wakes is its own timer gap, not a
    // dead socket. See observedSilenceMs.
    const observedSilence = observedSilenceMs(silentFor, wallGap);
    if (observedSilence > deadlineMs) {
      console.log(
        `hermes websocket silent for ${silentFor}ms (${observedSilence}ms observed after a ${wallGap}ms tick gap, > ${deadlineMs}ms) — assuming dead, reconnecting`
      );
      // Tear down the current socket; its onclose triggers reconnect, and the
      // reconnecting guard prevents a double attempt.
      // Silent past the AWOL window (e.g. lid closed) => the processrun is gone;
      // re-mint a fresh session on reconnect. A brief blip resumes the same session.
      const remint = silentFor >= browserAwolWindowMs;
      this.currentConn.then((conn) => {
        try {
          conn.webSocket.close();
        } catch {
          /* ignore */
        }
        this.reconnect(remint);
      });
    }
  }

  // Ask the server what IP it sees for us (the browser cannot read its own public
  // IP). Returns the IP, or undefined on any failure (network down / server
  // unreachable — which the liveness backstop will handle; a failed probe must
  // never itself trigger a rebuild).
  private async probeServerIp(): Promise<string | undefined> {
    try {
      const res = await fetch(`${this.rootUrl}/api/whoami`, {
        method: "GET",
        cache: "no-store",
      });
      if (!res.ok) return undefined;
      const data = await res.json();
      return typeof data?.ip === "string" ? data.ip : undefined;
    } catch {
      return undefined;
    }
  }

  // verifyTransport: the reusable transport-health check. Probe /api/whoami for
  // the server-seen IP and, if it CHANGED since last known, rebuild the WS
  // immediately (don't wait for the ~75s liveness timeout). Call it from any
  // trigger where the transport might have silently broken: wake
  // (visibilitychange), a stalled in-flight request, or the periodic backstop.
  //
  // CRUCIAL (glen): rebuild-WS and re-mint are INDEPENDENT decisions.
  //   - Rebuild the WS? <- did the network/socket change (this IP probe).
  //   - Re-mint the session? <- is the processrun past its AWOL window (the SAME
  //     silentFor >= browserAwolWindowMs check checkLiveness uses).
  // So an IP change caught fast (seconds after a WiFi switch) is almost always
  // still INSIDE the AWOL window -> rebuild + RESUME (same processUid, mailbox,
  // auth; new socket) — no re-mint, no churn. Only a change discovered past the
  // AWOL window re-mints. IP change never forces re-mint on its own.
  // FEATURE-20260715-browser-fast-detection-of-migrated-wifi.
  async verifyTransport(): Promise<void> {
    if (this.verifyingTransport || this.reconnecting) return;
    this.verifyingTransport = true;
    try {
      const ip = await this.probeServerIp();
      const prev = this.lastKnownIp;
      if (ip !== undefined) this.lastKnownIp = ip; // record only a successful probe
      const action = transportAction(
        prev,
        ip,
        Date.now() - this.lastInboundAt
      );
      if (action === "none") return;
      console.log(
        `hermes: server-seen IP changed ${prev} -> ${ip} (network migration) — rebuilding WS now` +
          (action === "remint"
            ? " + re-minting (past AWOL window)"
            : " and resuming the session")
      );
      const conn = await this.currentConn;
      try {
        conn.webSocket.close();
      } catch {
        /* ignore */
      }
      this.reconnect(action === "remint");
    } finally {
      this.verifyingTransport = false;
    }
  }

  mailbox(): Promise<CreateMailboxResponse> {
    return this.mailboxResponseP;
  }

  async withConn<T>(fn: (fn: HermesConnection) => T): Promise<T> {
    return this.currentConn.then((conn) => fn(conn));
  }

  async withConnP<T>(fn: (conn: HermesConnection) => Promise<T>): Promise<T> {
    return this.currentConn.then((conn) => fn(conn));
  }

  rawRpcCall(request: RawRpcRequest): Promise<Message> {
    // The timeout lives HERE, not just in HermesConnection.rawRpcCall, because
    // withConnP first awaits `currentConn` — and that promise never settles when
    // the connection cannot be established at all (e.g. createMailbox is rejected
    // 401 auth-expired, so no ClientSessionStarted ever arrives). In that state
    // conn.rawRpcCall is never even reached, so a timeout installed inside it can
    // never fire: the caller waits forever on a connection that will not come.
    // Bounding the WHOLE call covers both failure shapes — no connection, and a
    // connection whose reply never returns.
    // See tracker FEATURE-20260724-hermes-ws-reconnect-and-inflight-resend.
    const timeoutMs = rpcTimeoutMs(request.timeoutMs);
    return withTimeout(
      this.withConnP((conn) => conn.rawRpcCall(request)),
      timeoutMs,
      `RPC timed out after ${timeoutMs}ms (endPoint=${request.endPoint}) — no response, and possibly no live connection`,
    );
  }

  /**
   * Single request, streaming response, on whichever connection is current: the call opens once the
   * connection is there, and its frames are relayed as they come. Cancel reaches the call whether or
   * not it has opened yet.
   */
  rawRpcCallStreamingResponse(request: RawRpcRequest): RpcStreamCall {
    let inner: RpcStreamCall | undefined;
    let cancelled: string | undefined;
    const responses = new ReadableStream<Message>({
      start: async (controller) => {
        try {
          const conn = await this.currentConn;
          if (cancelled !== undefined) {
            controller.close();
            return;
          }
          inner = conn.rawRpcCallStreamingResponse(request);
          const reader = inner.responses.getReader();
          for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            controller.enqueue(value);
          }
          controller.close();
        } catch (e) {
          controller.error(e);
        }
      },
      cancel: (reason) => {
        cancelled = String(reason ?? "cancelled");
        inner?.cancel(cancelled);
      },
    });
    return {
      responses,
      cancel: (reason?: string) => {
        cancelled = reason ?? "cancelled";
        inner?.cancel(cancelled);
      },
    };
  }

  cdcSubscribe<A>(
    cdcs: CdcSubscription,
    listener: (cdcEvent: ChangeDataCaptureEvent, a: A) => void
  ): void {
    this.withConn((conn) => {
      conn.cdcSubscribe(cdcs, listener);
    });
  }

  rpcObserverSubscribe(
    readerKey: string,
    listener: (correlation: RpcRequestResponse) => void
  ): void {
    console.log("outer rpcObserverSubscribe", readerKey);
    this.withConn((conn) => {
      console.log("inner rpcObserverSubscribe", readerKey);
      conn.rpcObserverSubscribe(readerKey, listener);
    });
  }

  // rpcObserverTail correlates rpc-inbox + rpc-sent for a mailbox using the
  // binary tail HTTP endpoints instead of the websocket subscription. It builds the
  // same RpcRequestResponse rows the websocket observer does. Returns a stop() that
  // aborts both tails. Pure HTTP — no websocket connection needed.
  //
  // Address the mailbox EITHER by process run (preferred — /api/processrun_tail; the
  // server authorizes the run and resolves the mailbox, so the reader key never
  // reaches the browser) OR by mailbox key (/api/channel_tail; for callers that only
  // have a key).
  rpcObserverTail(
    target: { processUid: string } | { key: string },
    listener: (correlation: RpcRequestResponse) => void
  ): () => void {
    const tailTarget =
      "processUid" in target ? { processUid: target.processUid } : { key: target.key };
    const correlations = new Map<string, RpcRequestResponse>();
    const controller = new AbortController();

    const getOrCreate = (correlationId: string): RpcRequestResponse => {
      let c = correlations.get(correlationId);
      if (!c) {
        c = new RpcRequestResponse(correlationId);
        correlations.set(correlationId, c);
      }
      return c;
    };

    // rpc-inbox: each record is a Message (the response we received as client,
    // or a request we received as server).
    tailChannel({
      rootUrl: this.rootUrl,
      ...tailTarget,
      channel: "rpc-inbox",
      startPos: "first",
      authToken: this.authToken,
      signal: controller.signal,
      onRecord: ({ decoded, sequence, created }) => {
        const msg = decoded as Message;
        const correlationId = msg.header?.rpcHeader?.correlationId;
        if (correlationId) {
          const c = getOrCreate(correlationId);
          c.inboxMessage = msg;
          // Synthesize the envelope the timing/size accessors read. The raw tail
          // record carries no serverEnvelope (it lived in NATS metadata), so the
          // server sends the store time + sequence in the frame header.
          // channel-tail reads the frame header off the wire itself and yields
          // plain numbers; the proto's 64-bit fields are strings (forceLong=string).
          c.inboxEnvelope = {
            messageBytes: msg.data,
            serverEnvelope: {
              sequence: String(sequence),
              created: String(created),
              channel: "rpc-inbox",
              subscriptionId: "",
            },
          };
          listener(c);
        }
      },
      onError: (err) =>
        console.error("[rpcObserverTail] rpc-inbox tail error", err),
    });

    // rpc-sent: each record is a SendReceipt (a request we sent as client, or a
    // response we sent as server), carrying the request Message.
    tailChannel({
      rootUrl: this.rootUrl,
      ...tailTarget,
      channel: "rpc-sent",
      startPos: "first",
      authToken: this.authToken,
      signal: controller.signal,
      onRecord: ({ decoded, sequence, created }) => {
        const sr = decoded as SendReceipt;
        const msg = sr.request?.message;
        const correlationId = msg?.header?.rpcHeader?.correlationId;
        if (correlationId !== undefined) {
          const c = getOrCreate(correlationId);
          c.sentMessage = msg;
          c.sendReceipt = sr;
          c.sendReceiptEnvelope = {
            messageBytes: msg?.data ?? new Uint8Array(0),
            serverEnvelope: {
              sequence: String(sequence),
              created: String(created),
              channel: "rpc-sent",
              subscriptionId: "",
            },
          };
          listener(c);
        }
      },
      onError: (err) =>
        console.error("[rpcObserverTail] rpc-sent tail error", err),
    });

    return () => controller.abort();
  }

  continuumSubscribe(
    ns: ContinuumSubscription,
    listener: (me: MessageEnvelope) => void
  ): void {
    this.withConn((conn) => {
      conn.continuumSubscribe(ns, listener);
    });
  }

  channelMessageSubscribe(
    ms: MailboxSubscription,
    listener: (me: MessageEnvelope, msg: Message) => void
  ): void {
    this.withConn((conn) => {
      conn.channelMessageSubscribe(ms, listener);
    });
  }

  channelSendReceiptSubscribe(
    ms: MailboxSubscription,
    listener: (me: MessageEnvelope, msg: SendReceipt) => void
  ): void {
    this.withConn((conn) => {
      conn.channelSendReceiptSubscribe(ms, listener);
    });
  }

  sendPing(): void {
    this.withConn((conn) => {
      conn.sendPing();
    });
  }

  fetchServiceMappings(): Promise<ServiceMappings> {
    if (!_serviceMappingsCache) {
      _serviceMappingsCache = fetch(`${this.rootUrl}/api/service_mappings`)
        .then(r => {
          if (!r.ok) throw new Error(`Failed to fetch service mappings: ${r.status}`);
          return r.json();
        });
    }
    return _serviceMappingsCache;
  }
}

// ServerEnvelope.created is a unix-millis stamp, and a 64-bit proto field — so it
// decodes as a string (ts_proto forceLong=string). Millis are far below 2^53, so
// the timing accessors keep working in numbers; convert once, here.
function createdMillis(envelope: MessageEnvelope | undefined): number | undefined {
  const created = envelope?.serverEnvelope?.created;
  if (created === undefined || created === "") return undefined;
  const millis = Number(created);
  return Number.isFinite(millis) ? millis : undefined;
}

export class RpcRequestResponse {
  correlationId: string;

  sendReceiptEnvelope: MessageEnvelope | undefined;
  sendReceipt: SendReceipt | undefined;
  sentMessage: Message | undefined;

  inboxEnvelope: MessageEnvelope | undefined;
  inboxMessage: Message | undefined;

  constructor(correlationId: string) {
    this.correlationId = correlationId;
  }

  role(): string | undefined {
    const ic = this.isClient();
    if (ic) {
      return "client";
    } else if (ic === false) {
      return "server";
    }
  }

  contentType(): ContentType {
    const contentType =
      this.requestMessage()?.header?.contentType ??
      this.responseMessage()?.header?.contentType ??
      ContentType.UnspecifiedCT;
    return contentType;
  }

  isProtobuf(): boolean {
    return this.contentType() === ContentType.Protobuf;
  }

  isJson(): boolean {
    return this.contentType() === ContentType.Json;
  }

  isClient(): boolean | undefined {
    const inboxFrameType = this.inboxMessage?.header?.rpcHeader?.frameType;
    const sentFrameType = this.sentMessage?.header?.rpcHeader?.frameType;

    const isResponse = (ft?: RpcFrameType) =>
      ft === RpcFrameType.SuccessResponse || ft === RpcFrameType.ErrorResponse;

    // Client = we SENT a request and/or RECEIVED a response on our inbox.
    // Server = we RECEIVED a request and/or SENT a response.
    // Handle the one-sided captures too: a row may have only the inbox (a
    // response coming back to us = client) or only the sent side (a response we
    // emitted = server), not just the request frames.
    if (sentFrameType === RpcFrameType.Request || isResponse(inboxFrameType)) {
      return true;
    } else if (inboxFrameType === RpcFrameType.Request || isResponse(sentFrameType)) {
      return false;
    }
  }

  hasRequestAndResponse(): boolean {
    return this.sendReceiptEnvelope && this.inboxEnvelope ? true : false;
  }

  timeStarted(): Date | undefined {
    const time = this.timeStartedL();
    if (time) {
      return new Date(time);
    }
  }

  timeStartedL(): number | undefined {
    const ic = this.isClient();
    if (ic === true) {
      return createdMillis(this.sendReceiptEnvelope);
    } else if (ic === false) {
      return createdMillis(this.inboxEnvelope);
    }
    return undefined;
  }

  timeCompleted(): Date | undefined {
    const ic = this.isClient();
    let time: number | undefined = undefined;
    if (ic === false) {
      time = createdMillis(this.sendReceiptEnvelope);
    } else if (ic === true) {
      time = createdMillis(this.inboxEnvelope);
    }
    if (time) {
      return new Date(time);
    }
  }

  durationInMillis(): number | undefined {
    const ts = this.timeStarted()?.getTime();
    const tc = this.timeCompleted()?.getTime();
    if (ts && tc) {
      return tc - ts;
    }
  }

  endPoint(): string | undefined {
    // Prefer the request's endpoint, but fall back to whichever message we have
    // (the endpoint is the same across a correlation's request + response). This
    // keeps one-sided captures — e.g. a response-only row — from showing a blank
    // endpoint / "no endpoint".
    return (
      this.requestMessage()?.header?.rpcHeader?.endPoint ??
      this.sentMessage?.header?.rpcHeader?.endPoint ??
      this.inboxMessage?.header?.rpcHeader?.endPoint
    );
  }

  requestMessage(): Message | undefined {
    const ic = this.isClient();
    if (ic === true) {
      return this.sentMessage;
    } else if (ic === false) {
      return this.inboxMessage;
    }
  }

  requestEnvelope(): MessageEnvelope | undefined {
    const ic = this.isClient();
    if (ic === true) {
      return this.sendReceiptEnvelope;
    } else if (ic === false) {
      return this.inboxEnvelope;
    }
  }

  responseMessage(): Message | undefined {
    const ic = this.isClient();
    if (ic === true) {
      return this.inboxMessage;
    } else if (ic === false) {
      return this.sentMessage;
    }
  }

  responseEnvelope(): MessageEnvelope | undefined {
    const ic = this.isClient();
    if (ic === true) {
      return this.inboxEnvelope;
    } else if (ic === false) {
      return this.sendReceiptEnvelope;
    }
  }

  status(): string {
    const frameType = this.responseMessage()?.header?.rpcHeader?.frameType;
    if (!frameType) {
      return "";
    } else if (frameType === RpcFrameType.ErrorResponse) {
      return "error";
    } else if (frameType === RpcFrameType.SuccessResponse) {
      return "success";
    } else {
      return `Unexpected frame types ${frameType}`;
    }
  }

  async processSchema(reqOrResp: "request" | "response", data?: Uint8Array) {
    if (data === undefined || data.length === 0) {
      return {};
    }

    // The payload is JSON when the message says so (contentType=Json) OR when the
    // bytes themselves are clearly JSON. Many RPC messages carry JSON payloads
    // without setting contentType on the header, so we also sniff the first
    // non-whitespace byte. In all these cases the data is already JSON — decode it
    // locally instead of round-tripping the whole thing through the server's
    // protobuf-only /api/proto_to_json (which can't decode JSON and 500s).
    if (this.isJson() || looksLikeJson(data)) {
      const jsonStr = new TextDecoder().decode(data);
      return JSON.parse(jsonStr);
    }

    // Genuine protobuf payload: decode via the server using the endpoint schema.
    const endPoint = this.endPoint();
    if (endPoint === undefined) {
      return {
        error: "no endpoint",
      };
    }
    return protobufToJson(endPoint, reqOrResp, data);
  }

  async responseObj() {
    return this.processSchema("response", this.responseMessage()?.data);
  }

  async requestObj() {
    return this.processSchema("request", this.requestMessage()?.data);
  }
}

// Hermes URL from unified config. Resolved LAZILY: this module can be imported
// before loadClientConfig() populates config.hermes.url, so reading it at
// module-load time would capture the empty-string default — which makes
// `new URL("")` throw and turns API calls into relative requests against the
// frontend origin. Always build the URL from config.hermes.url at call time.
export function hermesUrl(): UrlBuilder {
  return new UrlBuilder(config.hermes.url);
}

// Select content type handler based on config (also read lazily).
function contentTypeHandlerFromConfig(): ContentTypeHandler {
  return config.hermes.contentType === 'protobuf'
    ? ProtobufContentTypeHandler
    : JsonContentTypeHandler;
}

const GlobalClient = {
  get: memoize(() => {
    const authToken = AuthTokenManager.getInstance().getToken();
    return newHermesClient(hermesUrl().toString(), contentTypeHandlerFromConfig(), authToken);
  }),
};

export default GlobalClient;

export function runHermesClientTest() {}

export function runHermesClientTest2() {
  // const hc = newHermesClient("https://hermes-go.ahsrcm.com", ContentType.Protobuf);
  const hc = newHermesClient(hermesUrl().toString(), JsonContentTypeHandler);
  hc.mailbox().then((_mbox) => {
    const cdcs: CdcSubscription = {
      tables: [
        {
          database: "continuum",
          table: "job",
        },
      ],
      startSeq: "new",
    };

    hc.cdcSubscribe(cdcs, (cdcEvent, _a) => {
      console.log("cdcEvent", cdcEvent);
    });
  });

  // hc.correlatedRpcReader("rrb07167144dc644a0be22a85301afea7e" , (correlation) => {
  //   console.log("correlation", correlation);
  // });
}

export interface CdcSubscription {
  tables: CdcTable[];
  startSeq?: string;
}

export interface CdcTable {
  database: string;
  table: string;
  primaryKeys?: string[]; // Optional for backward compatibility
}

/**
 * Resolve the payload bytes of a Message in place.
 *
 * When the server sends a large message, `Message.data` is left empty and
 * `Message.dataRef` holds the blob's URL on the engine data plane
 * (`.../api/blob/v1/{uid}`). The uid is a capability, so the fetch is
 * unauthenticated. After resolution, `msg.data` holds the bytes and
 * `msg.dataRef` is cleared so downstream code never has to know the blob
 * service exists.
 *
 * The ref is used verbatim. It was previously a bare `obj://mesh/<key>` that
 * this function URL-encoded into `/api/blob/{key}` — which the server then
 * re-prefixed, producing `obj://mesh/obj://mesh/<key>`. Large messages never
 * actually resolved in the browser.
 */
const blobFetchCache: Map<string, Promise<Uint8Array>> = new Map();

async function fetchBlob(ref: string): Promise<Uint8Array> {
  let p = blobFetchCache.get(ref);
  if (p === undefined) {
    p = (async () => {
      // dataRef is absolute when the mesh has a configured fetchUrlBase, and a
      // bare path otherwise; resolve the latter against the client root.
      const url = /^https?:\/\//.test(ref)
        ? ref
        : `${GlobalClient.get().rootUrl}${ref}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(
          `blob fetch failed for ${ref}: ${response.status} ${response.statusText}`,
        );
      }
      return new Uint8Array(await response.arrayBuffer());
    })();
    blobFetchCache.set(ref, p);
  }
  return p;
}

export async function resolveMessageData(
  msg: Message | undefined,
): Promise<Uint8Array | undefined> {
  if (msg === undefined) return undefined;
  if (msg.data !== undefined && msg.data.length > 0) return msg.data;
  const ref = msg.dataRef;
  if (!ref) return msg.data;
  msg.data = await fetchBlob(ref);
  msg.dataRef = "";
  return msg.data;
}

/**
 * Decode a Message from bytes and eagerly resolve `dataRef` -> `data` so
 * downstream consumers see a normal in-band payload.
 */
export async function decodeMessage(bytes: Uint8Array): Promise<Message> {
  const msg = Message.decode(bytes);
  await resolveMessageData(msg);
  return msg;
}

/**
 * Decode a SendReceipt and resolve any embedded Message's `dataRef`.
 */
export async function decodeSendReceipt(bytes: Uint8Array): Promise<SendReceipt> {
  const sr = SendReceipt.decode(bytes);
  await resolveMessageData(sr.request?.message);
  return sr;
}

// Heuristic: does this payload look like a JSON document? RPC messages frequently
// carry JSON data without setting contentType=Json on the header, so we sniff the
// first non-whitespace byte for '{' or '[' (an object or array — what these RPC
// request/response bodies serialize to).
function looksLikeJson(bytes: Uint8Array): boolean {
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i];
    // skip leading ASCII whitespace: space, tab, LF, CR
    if (b === 0x20 || b === 0x09 || b === 0x0a || b === 0x0d) {
      continue;
    }
    return b === 0x7b /* { */ || b === 0x5b /* [ */;
  }
  return false;
}

async function protobufToJson(
  schemaName: string,
  frametype: "request" | "response",
  bytes: Uint8Array
): Promise<string> {
  // const mboxObj = CreateMailboxRequest.toJSON(mbox);
  // const mboxJson = JSON.stringify(mboxObj);

  // let mailboxResponse: CreateMailboxResponse | undefined = undefined;
  const rootUrl = GlobalClient.get().rootUrl;
  const response = await fetch(
    `${rootUrl}/api/proto_to_json?schema=${schemaName}&frametype=${frametype}`,
    {
      method: "POST",
      body: new Blob([bytes.buffer as ArrayBuffer]),
    }
  );

  if (response.ok) {
    const jsonStr = await response.text();
    return JSON.parse(jsonStr);
  } else {
    throw new Error(`proto_to_json failed with status ${response.status}`);
  }
}

export interface ServiceMappings {
  readonly mappings: {
    readonly [key: LogicalMailbox]: MailboxAddress;
  };
}

let _serviceMappingsCache: Promise<ServiceMappings> | null = null;

export interface HermesClient {
  readonly rootUrl: string;
  mailbox(): Promise<CreateMailboxResponse>;
  // The GATEWAY-owned processrun uid this browser session is bound to, learned from
  // ClientSessionStarted. Empty until the first mint completes. Exposed so a caller (the
  // e2e browser probe, lifecycle UI) can read the identity the gateway assigned without
  // reaching into the impl.
  readonly browserProcessUid: string;
  rawRpcCall(request: RawRpcRequest): Promise<Message>;
  rawRpcCallStreamingResponse(request: RawRpcRequest): RpcStreamCall;
  cdcSubscribe<A>(
    cdcs: CdcSubscription,
    listener: (cdcEvent: ChangeDataCaptureEvent, a: A) => void
  ): void;
  rpcObserverSubscribe(
    readerKey: string,
    listener: (correlation: RpcRequestResponse) => void
  ): void;
  rpcObserverTail(
    target: { processUid: string } | { key: string },
    listener: (correlation: RpcRequestResponse) => void
  ): () => void;
  continuumSubscribe(
    ns: ContinuumSubscription,
    listener: (me: MessageEnvelope) => void
  ): void;
  withConn<T>(fn: (conn: HermesConnection) => T): Promise<T>;
  fetchServiceMappings(): Promise<ServiceMappings>;
}
